/**
 * Compliance Report PDF Generator with Real Screenshots
 * Generates professional PDF reports from compliance validation data
 * Captures actual 3D viewer screenshots for each validation finding
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { transformCloudScanToPerDoorReports } from '@/services/dataService';
import { PropertiesService } from '@/services/core/propertiesTools';
import { ViewerService } from '@/services/core/viewerTools';

interface RuleValidation {
  rule_number: string;
  rule_name: string;
  rule_raw_text: string;
  status: 'PASS' | 'FAIL' | 'INSUFFICIENT_DATA' | 'WARNING' | 'INCONCLUSIVE' | 'NOT_RUN';
  measured_value: number | string | null;
  required_value: string | null;
  unit: string | null;
  measurement_method: string | null;
  measurement_accuracy: string | null;
  llm_reasoning: string | null;
  involved_db_ids: number[];
  confidence: string;
  entities_used?: Array<{
    dbId: number;
    name?: string;
    role?: string;
    revit_id?: string;
    room_name?: string;
    room_number?: string;
    family_name?: string;
    family_type?: string;
  }>;
  // Per-row room data (may differ from door-level room data)
  room_name?: string | null;
  room_number?: string | null;
  // Per-standard status text from enriched data
  icc_status_text?: string | null;
  ada_status_text?: string | null;
  tas_status_text?: string | null;
  fbc_status_text?: string | null;
}

interface ComplianceReportData {
  report_type?: string | null;
  unit_name?: string | null;
  level?: string | null;
  wc_db_id?: number;
  door_db_id?: number;
  revit_id?: string | null;
  swing_x?: number | null;
  swing_y?: number | null;
  room_number?: string | null;
  room_name?: string | null;
  timestamp: string;
  overall_status: 'PASS' | 'FAIL' | 'WARNING' | 'INCONCLUSIVE' | 'NOT_RUN' | 'INSUFFICIENT_DATA';
  compliance_score?: string;
  rule_validations?: RuleValidation[];
  total_rules?: number;
  pass_count?: number;
  fail_count?: number;
  warning_count?: number;
  not_tested_count?: number;
  not_applicable_count?: number;
  insufficient_data_count?: number;
  executive_summary?: string;
  recommendations?: string[];
  // Room context for screenshots
  room_elements?: number[];  // All element IDs in the room/unit
  fixture_elements?: number[];  // Fixture element IDs for proper zoom
}

export class ComplianceReportPDF {
  /**
   * Generate PDF from JSON file with screenshots
   * @param jsonPath Path to the compliance report JSON file
   * @param reportType 'all' for all findings, 'problematic' for failures and warnings only
   * @param onProgress Progress callback
   */
  static async generateFromJSON(
    jsonPath: string,
    reportType: 'all' | 'problematic' = 'problematic',
    onProgress?: (progress: number, message: string) => void
  ): Promise<void> {
    try {
      onProgress?.(0, 'Loading compliance report...');

      const response = await fetch(jsonPath);
      if (!response.ok) {
        throw new Error(`Failed to load compliance report: ${response.statusText}`);
      }
      const data: ComplianceReportData = await response.json();

      await this.generateReport(data, reportType, onProgress);
    } catch (error) {
      console.error('Error generating PDF from JSON:', error);
      throw error;
    }
  }

  /**
   * Capture screenshot with consistent camera angle for PDF export
   * Always produces the same beautiful view regardless of current viewer state
   * @param viewer Forge viewer instance
   * @param involvedElementIds Elements involved in this validation (colored by status)
   * @param _roomElementIds All elements in the room (unused - kept for backwards compatibility)
   * @param status Validation status (for coloring: red=fail, green=pass, yellow=warning)
   * @param isDoor If true, use an angled view instead of top-down (doors appear as thin lines from above)
   * @param swingX Door swing direction X component (handle side)
   * @param swingY Door swing direction Y component (handle side)
   */
  private static async captureViewerScreenshot(
    viewer: any,
    involvedElementIds: number[],
    roomElementIds: number[],
    status: string,
    isDoor: boolean = false,
    swingX: number | null = null,
    swingY: number | null = null,
    skipFloorBand: boolean = false,
    skipNearbyElements: boolean = false,
  ): Promise<string> {
    // Pre-warm the Revit Ceilings cache so the Promise executor below can
    // use the synchronous isolate helper.
    try { await ViewerService.getCeilingDbIds(viewer); } catch { /* best-effort */ }

    return new Promise((resolve, reject) => {
      try {
        if (!involvedElementIds || involvedElementIds.length === 0) {
          reject(new Error('No element IDs provided'));
          return;
        }

        // Get THREE.js
        const THREE = (window as any).THREE;
        if (!THREE) {
          reject(new Error('THREE.js not available'));
          return;
        }

        // Save current viewer state to restore later
        const savedState = viewer.getState({ viewport: true });

        // Determine colors based on status - ALL elements get the same status color
        const statusLower = status.toLowerCase();
        let statusColor: any;
        if (statusLower === 'pass') {
          statusColor = new THREE.Vector4(0, 1, 0, 1.0); // Green
        } else if (statusLower === 'fail' || statusLower === 'failed') {
          statusColor = new THREE.Vector4(1, 0, 0, 1.0); // Red
        } else {
          statusColor = new THREE.Vector4(1, 1, 0, 1.0); // Yellow for warning/insufficient_data
        }

        // Isolate room context elements (walls, fixtures, etc.) so they're visible,
        // but only color the directly involved elements with the status color
        const hasRoomContext = roomElementIds.length > 0;
        const elementsToIsolate = hasRoomContext
          ? [...new Set([...roomElementIds, ...involvedElementIds])]
          : involvedElementIds;

        // Clear selection + ghosting + residual opacities in one call.
        ViewerService.prepareCleanIsolation(viewer);
        try { viewer.clearSelection(); } catch { /* noop */ }
        try {
          if (viewer.impl && typeof viewer.impl.setDisplayEdges === 'function') {
            viewer.impl.setDisplayEdges(false);
          }
        } catch { /* noop */ }

        // Light background — easy on the eyes in a PDF
        try {
          viewer.setBackgroundColor(245, 245, 248, 255, 255, 255);
        } catch { /* fallback if API unavailable */ }

        // STEP 3: focused (involved) + nearby context restricted to the
        // floor-to-(floor+2ft) band. Room elements (walls, fixtures) cross
        // the band and stay; ceilings & overhead objects drop out.
        // For multi-floor units the band filter can cull every involved
        // element (elements live on different floors); caller can pass
        // skipFloorBand=true to retry without culling.
        // skipNearbyElements=true → isolate ONLY the involved elements.
        // Used for doors, which are thin planes often embedded in walls;
        // including nearby walls causes them to occlude the door face.
        const nearbyRaw = hasRoomContext
          ? elementsToIsolate
          : (skipNearbyElements
              ? [...involvedElementIds]
              : ViewerService.findNearbyElements(viewer, involvedElementIds));
        const nearbyIds = skipFloorBand
          ? [...new Set([...nearbyRaw, ...involvedElementIds])]
          : ViewerService.filterByFloorBand(
              viewer,
              nearbyRaw,
              involvedElementIds,
              2
            );
        ViewerService.isolateWithTransparentCeilingsSync(viewer, nearbyIds, {
          focusedIds: involvedElementIds,
        });

        if (viewer.impl) {
          viewer.impl.sceneUpdated(true);
          viewer.impl.invalidate(true, true, true);
        }

        // Step 2: Apply status color to ALL involved elements after isolation completes
        setTimeout(() => {
          // Color ALL involved elements with the status color (red for fail, green for pass, yellow for warning)
          involvedElementIds.forEach((id: number) => {
            viewer.setThemingColor(id, statusColor);
          });

          if (viewer.impl) {
            viewer.impl.sceneUpdated(true);
            viewer.impl.invalidate(true, true, true);
          }

          // Step 3: Calculate bounding box and set up perfect camera angle
          setTimeout(() => {
            try {
              const model = viewer.model;
              if (!model) {
                viewer.fitToView(elementsToIsolate);
                return;
              }

              // Calculate bounding box of the INVOLVED elements only (not all room context)
              // so the camera focuses on the relevant objects, not the entire floor
              const fragList = model.getFragmentList();
              const bounds = new THREE.Box3();
              const instanceTree = model.getInstanceTree();

              involvedElementIds.forEach((dbId: number) => {
                if (instanceTree) {
                  instanceTree.enumNodeFragments(dbId, (fragId: number) => {
                    const fragBounds = new THREE.Box3();
                    fragList.getWorldBounds(fragId, fragBounds);
                    bounds.union(fragBounds);
                  }, true);
                }
              });

              if (bounds.isEmpty()) {
                viewer.fitToView(elementsToIsolate);
                return;
              }

              // Calculate center and size (on involved elements only)
              const center = bounds.getCenter(new THREE.Vector3());
              const size = bounds.getSize(new THREE.Vector3());

              // Determine which dimension is "up" (usually Z in architectural models)
              // and which is the longest horizontal dimension
              const horizontalSize = Math.max(size.x, size.y);
              const verticalSize = size.z;
              const maxDim = Math.max(horizontalSize, verticalSize);

              if (isDoor) {
                // Doors: moderate elevation (50°) + azimuth biased toward
                // the door face so the highlight is visible, while nearby
                // walls still frame the scene. Distance scales off the
                // door's vertical extent (84″ tall) so we're far enough
                // to see surrounding walls.
                const distance = Math.max(verticalSize, 36) * 4.0;
                const elevationAngle = 50 * (Math.PI / 180);
                let azimuthAngle = 0;
                if (swingX != null && swingY != null && (swingX !== 0 || swingY !== 0)) {
                  azimuthAngle = Math.atan2(swingY, swingX) + Math.PI;
                } else {
                  // No swing data: aim roughly perpendicular to the door's
                  // long horizontal axis, offset 20° so the view is not
                  // pure face-on (walls around the door enter the frame).
                  const perp = (size.x >= size.y) ? Math.PI / 2 : 0;
                  azimuthAngle = perp - (20 * Math.PI / 180);
                }

                const cameraX = center.x + distance * Math.cos(elevationAngle) * Math.cos(azimuthAngle);
                const cameraY = center.y + distance * Math.cos(elevationAngle) * Math.sin(azimuthAngle);
                const cameraZ = center.z + distance * Math.sin(elevationAngle);

                const newPosition = new THREE.Vector3(cameraX, cameraY, cameraZ);
                const upVector = new THREE.Vector3(0, 0, 1);

                viewer.navigation.setPosition(newPosition);
                viewer.navigation.setTarget(center);
                viewer.navigation.setCameraUpVector(upVector);

                const camera = viewer.navigation.getCamera();
                if (camera && camera.isPerspective) {
                  camera.fov = 40;
                  camera.updateProjectionMatrix();
                }
              } else if (!hasRoomContext) {
                // Skylight (no room context): bird's-eye view matching the
                // v15 reference.  Distance scales with involved bounds, so
                // corridors/bedrooms show the whole unit + surrounding
                // walls for context.  Doors are handled via isDoor=true
                // from the caller (different camera branch above).
                //
                //  - Long element (corridor, aspect > 2.5:1): look
                //    perpendicular to its length, 65° elevation, 2.5×.
                //  - Compact element (bedroom): 35° corner view, 70°, 2.0×.
                const isLong = Math.max(size.x, size.y) > Math.min(size.x, size.y) * 2.5;
                const elevationRad = (isLong ? 65 : 70) * (Math.PI / 180);
                const azimuthDeg = isLong ? ((size.x > size.y) ? 85 : 5) : 35;
                const azimuthRad = azimuthDeg * (Math.PI / 180);
                const distance = maxDim * (isLong ? 2.5 : 2.0);

                const cameraX = center.x + distance * Math.cos(elevationRad) * Math.cos(azimuthRad);
                const cameraY = center.y + distance * Math.cos(elevationRad) * Math.sin(azimuthRad);
                const cameraZ = center.z + distance * Math.sin(elevationRad);

                viewer.navigation.setPosition(new THREE.Vector3(cameraX, cameraY, cameraZ));
                viewer.navigation.setTarget(center);
                viewer.navigation.setCameraUpVector(new THREE.Vector3(0, 0, 1));

                const cam = viewer.navigation.getCamera();
                if (cam && cam.isPerspective) {
                  cam.fov = isLong ? 40 : 35;
                  cam.updateProjectionMatrix();
                }
              } else {
                // Room context: angled view focused on the involved elements
                const distance = maxDim * 1.6;
                const elevationAngle = 55 * (Math.PI / 180);
                const azimuthAngle = 30 * (Math.PI / 180);

                const cameraX = center.x + distance * Math.cos(elevationAngle) * Math.cos(azimuthAngle);
                const cameraY = center.y + distance * Math.cos(elevationAngle) * Math.sin(azimuthAngle);
                const cameraZ = center.z + distance * Math.sin(elevationAngle);

                const newPosition = new THREE.Vector3(cameraX, cameraY, cameraZ);
                const upVector = new THREE.Vector3(0, 0, 1);

                viewer.navigation.setPosition(newPosition);
                viewer.navigation.setTarget(center);
                viewer.navigation.setCameraUpVector(upVector);

                const camera = viewer.navigation.getCamera();
                if (camera && camera.isPerspective) {
                  camera.fov = 32;
                  camera.updateProjectionMatrix();
                }
              }

              viewer.impl.invalidate(true, true, true);

            } catch (cameraError) {
              console.debug('Camera setup failed, using fitToView:', cameraError);
              viewer.fitToView(elementsToIsolate);
            }

            // Step 4: Re-apply colors and wait for render
            setTimeout(() => {
              // Re-apply status color to ALL involved elements (can get cleared by camera changes)
              involvedElementIds.forEach((id: number) => {
                viewer.setThemingColor(id, statusColor);
              });

              if (viewer.impl) {
                viewer.impl.sceneUpdated(true);
                viewer.impl.invalidate(true, true, true);
              }

              // Step 5: Capture screenshot after full render
              setTimeout(() => {
                const width = viewer.container?.clientWidth || 1920;
                const height = viewer.container?.clientHeight || 1080;

                viewer.getScreenShot(width, height, (blobURL: string) => {
                  // Restore original viewer state after capture
                  try {
                    viewer.restoreState(savedState);
                  } catch (e) {
                    console.debug('Could not restore viewer state:', e);
                  }

                  fetch(blobURL)
                    .then((res) => res.blob())
                    .then((blob) => {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        resolve(reader.result as string);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(blob);
                    })
                    .catch(reject);
                });
              }, 800); // Wait for full render
            }, 500); // Wait for camera move
          }, 300); // Wait for colors
        }, 200); // Wait for isolation
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Skylight-only: capture a bird's-eye isometric screenshot that colors
   * every involved element by status and, for door rules (100.2), also
   * auto-detects each door's host wall (colored cyan semi-transparent).
   * Surrounding context includes all leaves in the visible area (walls,
   * casework, fixtures) on the same floor — not just walls.
   *
   * @param viewer Forge viewer instance
   * @param involvedDbIds Elements to highlight
   * @param status FAIL/WARNING/PASS (drives highlight color)
   * @param isDoorRule When true, per-element host wall is detected + cyan
   */
  private static async captureSkylightScreenshot(
    viewer: any,
    involvedDbIds: number[],
    status: string,
    isDoorRule: boolean,
  ): Promise<string> {
    const THREE = (window as any).THREE;
    if (!THREE) throw new Error('THREE.js not available');

    const model = viewer.model;
    const instanceTree = model?.getInstanceTree();
    const fragList = model?.getFragmentList();
    if (!model || !instanceTree || !fragList) throw new Error('viewer model not ready');

    const statusUp = (status || '').toUpperCase();
    const statusColor = (statusUp === 'PASS')
      ? new THREE.Vector4(0, 0.8, 0.1, 1)
      : (statusUp === 'FAIL' || statusUp === 'FAILED')
        ? new THREE.Vector4(1, 0, 0, 1)
        : new THREE.Vector4(1, 0.85, 0, 1); // WARNING/other
    const hostColor = new THREE.Vector4(0.1, 0.85, 0.95, 0.55); // cyan semi-transparent

    const getBBox = (id: number): any => {
      const b = new THREE.Box3();
      instanceTree.enumNodeFragments(id, (fragId: number) => {
        const fb = new THREE.Box3();
        fragList.getWorldBounds(fragId, fb);
        b.union(fb);
      }, true);
      return b;
    };

    // Fully reset any prior isolation/theming from a previous capture.
    // Without this, state leaks between units and subsequent screenshots
    // render as tiny specks or fail to show nearby elements at all.
    try { viewer.clearSelection?.(); } catch { /* noop */ }
    try { viewer.showAll?.(); } catch { /* noop */ }
    try { viewer.clearThemingColors?.(viewer.model); } catch { /* noop */ }
    // Also clear any per-dbId theming tracked from previous Skylight
    // captures (involved/host/nearby dbIds). Avoids the expensive 400K
    // iteration while still resetting everything we might have coloured.
    const prev = (ComplianceReportPDF as any)._skylightPrevThemedIds as Set<number> | undefined;
    if (prev && prev.size > 0) {
      for (const id of prev) {
        try { viewer.setThemingColor(id, null); } catch { /* noop */ }
      }
    }
    viewer.impl?.sceneUpdated?.(true);
    viewer.impl?.invalidate?.(true, true, true);
    // Give the viewer a frame to process showAll + theming reset before
    // we start isolating/colouring for this capture.
    await new Promise(r => setTimeout(r, 300));

    // Filter to same-floor dbIds so multi-floor units (e.g. "_FLS 3-11")
    // don't explode the bbox across levels.
    const validIds = involvedDbIds.filter(id => {
      const b = getBBox(id);
      return !b.isEmpty();
    });
    if (validIds.length === 0) throw new Error('no valid involved elements');
    const firstCenter = getBBox(validIds[0]).getCenter(new THREE.Vector3());
    const sameFloorIds = validIds.filter(id => {
      const c = getBBox(id).getCenter(new THREE.Vector3());
      return Math.abs(c.z - firstCenter.z) <= 6;
    });
    const sfIds = sameFloorIds.length > 0 ? sameFloorIds : [validIds[0]];

    // Cluster: keep only same-floor elements within 15 ft horizontal
    // distance of the first one. Stops bbox from ballooning when a unit
    // has elements scattered across multiple bedrooms/rooms.
    const firstSf = getBBox(sfIds[0]).getCenter(new THREE.Vector3());
    const involved = sfIds.filter(id => {
      const c = getBBox(id).getCenter(new THREE.Vector3());
      return Math.hypot(c.x - firstSf.x, c.y - firstSf.y) <= 15;
    });

    // Per-element bboxes
    const involvedBBoxes = involved.map(id => getBBox(id));
    const involvedUnion = new THREE.Box3();
    for (const b of involvedBBoxes) involvedUnion.union(b);
    // Floor band from involved min.z (floor) up ~11 ft (ceiling + slack)
    const floorMin = involvedUnion.min.z - 1.5;
    const floorMax = involvedUnion.min.z + 11;

    // For door rules, detect a host wall per-door (strict then loose)
    const WALL_LEAF_RE = /^Basic Wall(?: \[\d+\])+$/;
    const hostIds = new Set<number>();
    const hostUnion = new THREE.Box3();
    if (isDoorRule) {
      for (const doorId of involved) {
        const door = getBBox(doorId);
        const ds = door.getSize(new THREE.Vector3());
        const dc = door.getCenter(new THREE.Vector3());
        const doorThinIsY = ds.y < ds.x;
        let strict: { id: number; bbox: any; score: number } | null = null;
        let loose: { id: number; bbox: any; score: number } | null = null;
        for (let id = 1; id < 400000; id++) {
          const name = instanceTree.getNodeName(id);
          if (!name || !WALL_LEAF_RE.test(name)) continue;
          const b = getBBox(id);
          if (b.isEmpty()) continue;
          const wc = b.getCenter(new THREE.Vector3());
          const ws = b.getSize(new THREE.Vector3());
          if (Math.abs(wc.z - dc.z) > 4) continue;
          if (ws.z < 6 || ws.z > 18) continue;
          // Strict: matching thin-axis + containing door center horizontally
          const wallThinIsY = ws.y < ws.x;
          if (wallThinIsY === doorThinIsY) {
            const thinAxis: 'x' | 'y' = doorThinIsY ? 'y' : 'x';
            const longAxis: 'x' | 'y' = doorThinIsY ? 'x' : 'y';
            const thinTol = Math.max(ws[thinAxis], 0.5) / 2 + 0.3;
            if (
              Math.abs(dc[thinAxis] - wc[thinAxis]) <= thinTol &&
              dc[longAxis] >= b.min[longAxis] - 0.3 &&
              dc[longAxis] <= b.max[longAxis] + 0.3
            ) {
              const score = Math.abs(dc[thinAxis] - wc[thinAxis]);
              if (!strict || score < strict.score) strict = { id, bbox: b, score };
            }
          }
          // Loose: closest wall within 4ft (fallback)
          const dxy = Math.hypot(wc.x - dc.x, wc.y - dc.y);
          if (dxy <= 4 && (!loose || dxy < loose.score)) loose = { id, bbox: b, score: dxy };
        }
        const chosen = strict || loose;
        if (chosen && !hostIds.has(chosen.id)) {
          hostIds.add(chosen.id);
          hostUnion.union(chosen.bbox);
        }
      }
    }

    // Find nearby context on the same floor. Broad whitelist of wall +
    // fixture/casework families so scenes aren't just bare walls. Floor
    // band is derived from involved min.z up 11 ft (ceiling range).
    const KEEP_RE = /^Basic Wall \[|^HW_|^\(\d+\)_|^Fin |^Low Boy|^Window|^Casework|^Furniture|^Plumbing|^Fixture|^Cabinet|^Bath|^Toilet|^Shower|^Sink|^Countertop/;
    const radius = 14; // ft around each involved element
    const nearbyIds: number[] = [];
    const involvedCenters = involvedBBoxes.map(b => b.getCenter(new THREE.Vector3()));
    const involvedSet = new Set(involved);
    for (let id = 1; id < 400000 && nearbyIds.length < 150; id++) {
      if (involvedSet.has(id) || hostIds.has(id)) continue;
      const name = instanceTree.getNodeName(id);
      if (!name || !KEEP_RE.test(name)) continue;
      const b = getBBox(id);
      if (b.isEmpty()) continue;
      // Floor band overlap — excludes other floors and tall shaft walls
      if (b.max.z < floorMin || b.min.z > floorMax) continue;
      const s = b.getSize(new THREE.Vector3());
      if (s.z > 18 || s.x > 60 || s.y > 60) continue;
      const bc = b.getCenter(new THREE.Vector3());
      let inRange = false;
      for (const c of involvedCenters) {
        if (Math.hypot(bc.x - c.x, bc.y - c.y) <= radius) { inRange = true; break; }
      }
      if (!inRange) continue;
      nearbyIds.push(id);
    }

    const savedState = viewer.getState({ viewport: true });

    try { viewer.clearSelection(); } catch { /* noop */ }
    try { viewer.impl?.setDisplayEdges?.(false); } catch { /* noop */ }
    try { viewer.setBackgroundColor(245, 245, 248, 255, 255, 255); } catch { /* noop */ }

    const isolate = Array.from(new Set([
      ...involved,
      ...Array.from(hostIds),
      ...nearbyIds,
    ]));
    viewer.isolate(isolate);

    const applyColors = () => {
      for (const id of involved) viewer.setThemingColor(id, statusColor);
      for (const id of hostIds) viewer.setThemingColor(id, hostColor);
    };
    applyColors();
    // Record which ids we coloured so the next capture can reset just
    // these instead of iterating the whole model.
    const themedNow = new Set<number>();
    for (const id of involved) themedNow.add(id);
    for (const id of hostIds) themedNow.add(id);
    (ComplianceReportPDF as any)._skylightPrevThemedIds = themedNow;
    viewer.impl?.sceneUpdated(true);
    viewer.impl?.invalidate(true, true, true);

    // Camera: bird's-eye isometric framed on involved + host walls
    const combined = involvedUnion.clone();
    if (!hostUnion.isEmpty()) combined.union(hostUnion);
    const cc = combined.getCenter(new THREE.Vector3());
    const cs = combined.getSize(new THREE.Vector3());
    const maxDim = Math.max(cs.x, cs.y, cs.z);

    const elev = 75 * (Math.PI / 180);
    const azim = 30 * (Math.PI / 180);
    const mul = hostIds.size > 0 ? 2.5 : 1.8;
    const distance = Math.min(Math.max(maxDim * mul, 15), 45);
    viewer.navigation.setPosition(new THREE.Vector3(
      cc.x + distance * Math.cos(elev) * Math.cos(azim),
      cc.y + distance * Math.cos(elev) * Math.sin(azim),
      cc.z + distance * Math.sin(elev),
    ));
    viewer.navigation.setTarget(cc);
    viewer.navigation.setCameraUpVector(new THREE.Vector3(0, 0, 1));
    const cam = viewer.navigation.getCamera();
    if (cam?.isPerspective) { cam.fov = 38; cam.updateProjectionMatrix(); }
    viewer.impl?.invalidate(true, true, true);

    await new Promise(r => setTimeout(r, 1400));
    applyColors(); // re-apply; camera ops can clear theming
    viewer.impl?.sceneUpdated(true);
    viewer.impl?.invalidate(true, true, true);
    await new Promise(r => setTimeout(r, 700));

    const w = viewer.container?.clientWidth || 1400;
    const h = viewer.container?.clientHeight || 900;
    const dataUrl: string = await new Promise((resolve, reject) => {
      viewer.getScreenShot(w, h, (blobUrl: string) => {
        fetch(blobUrl).then(r => r.blob()).then(b => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(fr.result as string);
          fr.onerror = reject;
          fr.readAsDataURL(b);
        }).catch(reject);
      });
    });

    try { viewer.restoreState(savedState); } catch { /* noop */ }
    return dataUrl;
  }

  /**
   * Capture screenshots for all validation findings
   * @param data Compliance report data
   * @param reportType 'all' for all findings, 'problematic' for failures and warnings only
   * @param onProgress Progress callback
   */
  private static async captureAllScreenshots(
    data: ComplianceReportData,
    reportType: 'all' | 'problematic',
    onProgress?: (progress: number, message: string) => void
  ): Promise<Map<string, string>> {
    const screenshots = new Map<string, string>();

    // Get viewer from global scope
    const viewer = (window as any).NOP_VIEWER as Autodesk.Viewing.GuiViewer3D;

    if (!viewer) {
      console.warn('Forge Viewer not available, skipping screenshot capture');
      return screenshots;
    }

    const validations = data.rule_validations || [];

    // Get room context from compliance report
    const roomElements = data.room_elements || [];

    // Filter validations based on report type
    const validationsToCapture = validations.filter((v) => {
      const hasElements = v.involved_db_ids && v.involved_db_ids.length > 0;

      if (reportType === 'all') {
        // Capture all validations with elements
        return hasElements;
      } else {
        // Capture only problematic findings (FAIL, WARNING, INSUFFICIENT_DATA)
        return hasElements && (
          v.status === 'FAIL' ||
          v.status === 'WARNING' ||
          v.status === 'INSUFFICIENT_DATA'
        );
      }
    });

    let captureCount = 0;
    const totalCaptures = validationsToCapture.length;

    for (const validation of validationsToCapture) {
      try {
        captureCount++;
        const progress = 10 + Math.floor((captureCount / totalCaptures) * 60); // 10% to 70%
        onProgress?.(
          progress,
          `Capturing screenshot ${captureCount} of ${totalCaptures}...`
        );

        const imageData = await this.captureViewerScreenshot(
          viewer,
          validation.involved_db_ids,
          roomElements,
          validation.status
        );

        screenshots.set(validation.rule_number, imageData);
      } catch (error) {
        console.error(`Failed to capture screenshot for rule ${validation.rule_number}:`, error);
        // Continue with other screenshots even if one fails
      }
    }

    return screenshots;
  }

  /**
   * Generate PDF report from compliance data with real screenshots
   * @param data Compliance report data
   * @param reportType 'all' for all findings, 'problematic' for failures and warnings only
   * @param onProgress Progress callback
   */
  static async generateReport(
    data: ComplianceReportData,
    reportType: 'all' | 'problematic' = 'problematic',
    onProgress?: (progress: number, message: string) => void
  ): Promise<void> {
    try {
      // Capture screenshots first
      onProgress?.(5, 'Capturing 3D viewer screenshots...');
      const screenshots = await this.captureAllScreenshots(data, reportType, onProgress);

      onProgress?.(75, 'Generating PDF document...');

      // Generate PDF using section-by-section approach to avoid page cuts
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 10; // mm
      const contentWidth = pageWidth - (margin * 2);
      let currentY = margin;

      // Helper function to render HTML section to canvas and add to PDF
      const addSectionToPDF = async (html: string, isFirstSection: boolean = false): Promise<void> => {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.width = `${contentWidth}mm`;
        container.style.backgroundColor = 'white';
        container.innerHTML = `<style>${this.getStyles()}</style>${html}`;
        document.body.appendChild(container);

        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });

        document.body.removeChild(container);

        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Check if section fits on current page
        if (!isFirstSection && currentY + imgHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }

        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 5; // 5mm spacing between sections
      };

      onProgress?.(78, 'Rendering header...');

      // Add header section
      await addSectionToPDF(this.generateHeaderHTML(data), true);

      onProgress?.(80, 'Rendering summary...');

      // Add summary section
      await addSectionToPDF(this.generateSummaryHTML(data));

      // Get validations based on report type
      const validations = data.rule_validations || [];
      const failedRules = validations.filter(r => r.status === 'FAIL');
      const insufficientRules = validations.filter(r => r.status === 'INSUFFICIENT_DATA');
      const passedRules = validations.filter(r => r.status === 'PASS');

      // Determine which rules to include based on report type
      const rulesToInclude = reportType === 'all'
        ? [...failedRules, ...insufficientRules, ...passedRules]
        : [...failedRules, ...insufficientRules];

      const totalRules = rulesToInclude.length;
      let ruleIndex = 0;

      // Add section headers and rules - combine title with first rule to keep them together
      if (failedRules.length > 0) {
        for (let i = 0; i < failedRules.length; i++) {
          const rule = failedRules[i];
          ruleIndex++;
          const progress = 80 + Math.floor((ruleIndex / totalRules) * 15);
          onProgress?.(progress, `Rendering rule ${ruleIndex} of ${totalRules}...`);

          const screenshotData = screenshots.get(rule.rule_number);

          // For the first rule, include the section title
          if (i === 0) {
            const titleAndCard = `
              <div class="section-title" style="color: #dc2626;">Failed Requirements</div>
              ${this.generateRuleCardHTML(rule, screenshotData, 'fail')}
            `;
            await addSectionToPDF(titleAndCard);
          } else {
            await addSectionToPDF(this.generateRuleCardHTML(rule, screenshotData, 'fail'));
          }
        }
      }

      if (insufficientRules.length > 0) {
        for (let i = 0; i < insufficientRules.length; i++) {
          const rule = insufficientRules[i];
          ruleIndex++;
          const progress = 80 + Math.floor((ruleIndex / totalRules) * 15);
          onProgress?.(progress, `Rendering rule ${ruleIndex} of ${totalRules}...`);

          const screenshotData = screenshots.get(rule.rule_number);

          // For the first rule, include the section title
          if (i === 0) {
            const titleAndCard = `
              <div class="section-title" style="color: #d97706;">Requirements with Insufficient Data</div>
              ${this.generateRuleCardHTML(rule, screenshotData, 'warning')}
            `;
            await addSectionToPDF(titleAndCard);
          } else {
            await addSectionToPDF(this.generateRuleCardHTML(rule, screenshotData, 'warning'));
          }
        }
      }

      if (reportType === 'all' && passedRules.length > 0) {
        for (let i = 0; i < passedRules.length; i++) {
          const rule = passedRules[i];
          ruleIndex++;
          const progress = 80 + Math.floor((ruleIndex / totalRules) * 15);
          onProgress?.(progress, `Rendering rule ${ruleIndex} of ${totalRules}...`);

          const screenshotData = screenshots.get(rule.rule_number);

          // For the first rule, include the section title
          if (i === 0) {
            const titleAndCard = `
              <div class="section-title" style="color: #059669;">Passed Requirements</div>
              ${this.generateRuleCardHTML(rule, screenshotData, 'pass')}
            `;
            await addSectionToPDF(titleAndCard);
          } else {
            await addSectionToPDF(this.generateRuleCardHTML(rule, screenshotData, 'pass'));
          }
        }
      }

      // Add recommendations section if there are any
      if (data.recommendations && data.recommendations.length > 0) {
        await addSectionToPDF(this.generateRecommendationsHTML(data));
      }

      onProgress?.(98, 'Finalizing PDF...');

      // Generate filename
      const timestamp = new Date(data.timestamp).toISOString().split('T')[0];
      const elementId = data.wc_db_id || data.door_db_id || 'unknown';
      const elementType = data.wc_db_id ? 'WC' : 'Door';
      const filename = `ADA-Compliance-Report-${elementType}${elementId}-${timestamp}.pdf`;

      onProgress?.(100, 'Download ready!');

      // Save the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  /**
   * Get CSS styles for PDF sections
   */
  private static getStyles(): string {
    return `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.5;
      color: #1f2937;
      background: white;
    }

    .header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 3px solid #4f46e5;
    }

    .header h1 {
      font-size: 22pt;
      color: #1e1b4b;
      margin-bottom: 8px;
    }

    .header-meta {
      font-size: 10pt;
      color: #6b7280;
    }

    .section-title {
      font-size: 14pt;
      font-weight: 700;
      margin: 15px 0 10px 0;
      padding: 8px 12px;
      background: linear-gradient(90deg, #f3f4f6, transparent);
      border-left: 4px solid currentColor;
    }

    .summary-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin-bottom: 15px;
    }

    .summary-card {
      padding: 12px;
      border-radius: 8px;
      text-align: center;
    }

    .summary-card.overall {
      background: linear-gradient(135deg, #eef2ff, #e0e7ff);
      border: 1px solid #c7d2fe;
    }

    .summary-card.pass {
      background: linear-gradient(135deg, #ecfdf5, #d1fae5);
      border: 1px solid #a7f3d0;
    }

    .summary-card.fail {
      background: linear-gradient(135deg, #fef2f2, #fee2e2);
      border: 1px solid #fecaca;
    }

    .summary-card.warning {
      background: linear-gradient(135deg, #fffbeb, #fef3c7);
      border: 1px solid #fde68a;
    }

    .summary-value {
      font-size: 20pt;
      font-weight: 700;
    }

    .summary-label {
      font-size: 9pt;
      color: #6b7280;
      margin-top: 4px;
    }

    .executive-summary {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 15px;
    }

    .executive-summary h3 {
      font-size: 12pt;
      color: #334155;
      margin-bottom: 8px;
    }

    .rule-card {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 10px;
    }

    .rule-card.fail {
      border-left: 4px solid #ef4444;
      background: linear-gradient(90deg, #fef2f2, white);
    }

    .rule-card.warning {
      border-left: 4px solid #f59e0b;
      background: linear-gradient(90deg, #fffbeb, white);
    }

    .rule-card.pass {
      border-left: 4px solid #10b981;
      background: linear-gradient(90deg, #ecfdf5, white);
    }

    .rule-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 14px;
    }

    .rule-number {
      font-size: 11pt;
      font-weight: 600;
      color: #4f46e5;
    }

    .rule-name {
      font-size: 13pt;
      font-weight: 700;
      color: #1f2937;
      margin-top: 2px;
    }

    .status-badge {
      padding: 8px 20px;
      border-radius: 6px;
      font-size: 14pt;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      border: 2px solid;
    }

    .status-badge.fail {
      background: #fee2e2;
      color: #b91c1c;
      border-color: #f87171;
    }

    .status-badge.warning {
      background: #fef3c7;
      color: #92400e;
      border-color: #fbbf24;
    }

    .status-badge.pass {
      background: #d1fae5;
      color: #065f46;
      border-color: #34d399;
    }

    .rule-text {
      font-size: 10pt;
      color: #4b5563;
      background: #f9fafb;
      padding: 10px;
      border-left: 3px solid #d1d5db;
      margin: 10px 0;
    }

    .measurement-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      margin: 10px 0;
    }

    .measurement-item {
      font-size: 10pt;
    }

    .measurement-label {
      color: #6b7280;
    }

    .measurement-value {
      font-weight: 600;
      color: #374151;
    }

    .reasoning {
      font-size: 10pt;
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 6px;
      padding: 10px;
      margin: 10px 0;
      color: #0c4a6e;
    }

    .screenshot-container {
      margin-top: 10px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 8px;
      text-align: center;
      page-break-inside: avoid;
    }

    .screenshot-title {
      font-size: 9pt;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 6px;
    }

    .screenshot-image {
      width: 100%;
      max-height: 300px;
      object-fit: contain;
      border-radius: 4px;
    }

    .screenshot-placeholder {
      background: #f9fafb;
      border: 2px dashed #d1d5db;
      border-radius: 6px;
      padding: 30px;
      color: #9ca3af;
    }

    .element-ids {
      font-size: 9pt;
      color: #6b7280;
      margin-top: 8px;
    }

    .recommendations {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      padding: 15px;
      margin-top: 15px;
    }

    .recommendations h3 {
      font-size: 12pt;
      color: #b91c1c;
      margin-bottom: 10px;
    }

    .recommendations ul {
      list-style-type: none;
      padding: 0;
    }

    .recommendations li {
      font-size: 10pt;
      padding: 6px 0;
      border-bottom: 1px solid #fee2e2;
    }

    .recommendations li:last-child {
      border-bottom: none;
    }
    `;
  }

  /**
   * Generate header HTML section
   */
  private static generateHeaderHTML(data: ComplianceReportData): string {
    const timestamp = new Date(data.timestamp).toLocaleString();
    const elementId = data.wc_db_id || data.door_db_id || 'N/A';
    const elementType = data.wc_db_id ? 'Water Closet' : 'Door';

    // Build room info with both number and name
    let roomInfo = 'N/A';
    if (data.room_number || data.room_name) {
      const parts = [];
      if (data.room_number) parts.push(data.room_number);
      if (data.room_name) parts.push(data.room_name);
      roomInfo = parts.join(' - ');
    }

    return `
      <div class="header">
        <h1>ADA Compliance Validation Report</h1>
        <div class="header-meta">
          ${elementType} ID: ${elementId} | Room: ${roomInfo} | Generated: ${timestamp}
        </div>
      </div>
    `;
  }

  /**
   * Generate summary HTML section
   */
  private static generateSummaryHTML(data: ComplianceReportData): string {
    const statusColor = data.overall_status === 'PASS' ? '#059669' :
                       data.overall_status === 'FAIL' ? '#dc2626' : '#d97706';

    return `
      <div class="summary-grid">
        <div class="summary-card overall">
          <div class="summary-value" style="color: ${statusColor};">${data.overall_status}</div>
          <div class="summary-label">Overall Status</div>
        </div>
        <div class="summary-card pass">
          <div class="summary-value" style="color: #059669;">${data.pass_count || 0}</div>
          <div class="summary-label">Passed</div>
        </div>
        <div class="summary-card fail">
          <div class="summary-value" style="color: #dc2626;">${data.fail_count || 0}</div>
          <div class="summary-label">Failed</div>
        </div>
        <div class="summary-card warning">
          <div class="summary-value" style="color: #d97706;">${data.insufficient_data_count || 0}</div>
          <div class="summary-label">Insufficient Data</div>
        </div>
      </div>
      ${data.executive_summary ? `
        <div class="executive-summary">
          <h3>Executive Summary</h3>
          <p>${data.executive_summary}</p>
        </div>
      ` : ''}
    `;
  }

  /**
   * Generate rule card HTML
   */
  private static generateRuleCardHTML(
    rule: RuleValidation,
    screenshotData: string | undefined,
    cardType: 'fail' | 'warning' | 'pass'
  ): string {
    const statusLabel = rule.status === 'INSUFFICIENT_DATA' ? 'INSUFFICIENT DATA' : rule.status;

    return `
      <div class="rule-card ${cardType}">
        <div class="rule-header">
          <div>
            <div class="rule-number">ADA ${rule.rule_number}</div>
            <div class="rule-name">${rule.rule_name}</div>
          </div>
          <span class="status-badge ${cardType}">${statusLabel}</span>
        </div>

        <div class="rule-text">${rule.rule_raw_text}</div>

        ${rule.measured_value !== null || rule.required_value ? `
          <div class="measurement-grid">
            ${rule.measured_value !== null ? `
              <div class="measurement-item">
                <span class="measurement-label">Measured: </span>
                <span class="measurement-value">${rule.measured_value}${rule.unit ? ` ${rule.unit}` : ''}</span>
              </div>
            ` : ''}
            ${rule.required_value ? `
              <div class="measurement-item">
                <span class="measurement-label">Required: </span>
                <span class="measurement-value">${rule.required_value}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}

        ${rule.llm_reasoning ? `
          <div class="reasoning">
            <strong>Analysis:</strong> ${rule.llm_reasoning}
          </div>
        ` : ''}

        <div class="screenshot-container">
          <div class="screenshot-title">3D Viewer Screenshot</div>
          ${screenshotData ? `
            <img src="${screenshotData}" alt="Screenshot for ${rule.rule_name}" class="screenshot-image" />
          ` : `
            <div class="screenshot-placeholder">Screenshot not available</div>
          `}
          ${rule.involved_db_ids && rule.involved_db_ids.length > 0 ? `
            <div class="element-ids">Element IDs: ${rule.involved_db_ids.join(', ')}</div>
          ` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Generate recommendations HTML
   */
  private static generateRecommendationsHTML(data: ComplianceReportData): string {
    if (!data.recommendations || data.recommendations.length === 0) {
      return '';
    }

    return `
      <div class="recommendations">
        <h3>Critical Actions Required</h3>
        <ul>
          ${data.recommendations.map(rec => `<li>${rec}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  /**
   * Normalize report data from v4 or v5 format to unified format
   * v4 format: regulation_validation_results, validation_metadata
   * v5 format: rule_validations, wc_db_id
   */
  private static normalizeReportData(rawData: any): ComplianceReportData {
    // Check if it's v4 format (has regulation_validation_results)
    if (rawData.regulation_validation_results) {
      const validations = rawData.regulation_validation_results || [];
      const metadata = rawData.validation_metadata || {};
      const roomSummary = rawData.room_summary || {};

      // Extract all element IDs from room_summary for screenshots
      const roomElements: number[] = [];
      const fixtureElements: number[] = [];

      // Water closet
      if (roomSummary.water_closet?.dbId) {
        roomElements.push(roomSummary.water_closet.dbId);
        fixtureElements.push(roomSummary.water_closet.dbId);
      }

      // Walls
      if (roomSummary.water_closet?.wall_distances?.rear_wall?.dbId) {
        roomElements.push(roomSummary.water_closet.wall_distances.rear_wall.dbId);
      }
      if (roomSummary.water_closet?.wall_distances?.side_wall?.dbId) {
        roomElements.push(roomSummary.water_closet.wall_distances.side_wall.dbId);
      }

      // Grab bars
      if (roomSummary.grab_bars?.horizontal_side_wall?.dbId) {
        roomElements.push(roomSummary.grab_bars.horizontal_side_wall.dbId);
        fixtureElements.push(roomSummary.grab_bars.horizontal_side_wall.dbId);
      }
      if (roomSummary.grab_bars?.vertical_side_wall?.dbId) {
        roomElements.push(roomSummary.grab_bars.vertical_side_wall.dbId);
        fixtureElements.push(roomSummary.grab_bars.vertical_side_wall.dbId);
      }
      if (roomSummary.grab_bars?.rear_wall?.dbId) {
        roomElements.push(roomSummary.grab_bars.rear_wall.dbId);
        fixtureElements.push(roomSummary.grab_bars.rear_wall.dbId);
      }

      // Other fixtures (tub, shower, sink, etc.)
      if (roomSummary.other_fixtures && Array.isArray(roomSummary.other_fixtures)) {
        roomSummary.other_fixtures.forEach((fixture: any) => {
          if (fixture.dbId) {
            roomElements.push(fixture.dbId);
            fixtureElements.push(fixture.dbId);
          }
        });
      }

      // Default involved_db_ids is the water closet for toilet validations
      const defaultInvolvedDbIds = roomSummary.water_closet?.dbId ? [roomSummary.water_closet.dbId] : [];

      let passCount = 0;
      let failCount = 0;
      let insufficientCount = 0;

      validations.forEach((v: any) => {
        const status = (v.status || '').toUpperCase();
        if (status === 'PASS') passCount++;
        else if (status === 'FAIL') failCount++;
        else if (status === 'INSUFFICIENT_DATA') insufficientCount++;
      });

      let overallStatus: 'PASS' | 'FAIL' | 'WARNING' | 'INCONCLUSIVE' | 'NOT_RUN' | 'INSUFFICIENT_DATA' = 'PASS';
      if (failCount > 0) overallStatus = 'FAIL';
      else if (insufficientCount > 0) overallStatus = 'INSUFFICIENT_DATA';

      const ruleValidations: RuleValidation[] = validations.map((v: any) => ({
        rule_number: v.regulation_id || v.rule_number || 'N/A',
        rule_name: v.title || v.rule_name || 'Unknown Rule',
        rule_raw_text: v.regulation_text || v.rule_raw_text || v.requirement || '',
        status: (v.status || 'NOT_RUN').toUpperCase() as any,
        measured_value: v.measured_value ?? null,
        required_value: v.required_value || v.requirement || null,
        unit: v.unit || null,
        measurement_method: v.measurement_method || null,
        measurement_accuracy: v.measurement_accuracy || null,
        llm_reasoning: v.notes || v.llm_reasoning || null,
        // Use validation's involved_db_ids if present, then entities_used dbIds, then default (water closet)
        // Only include the water closet as default for toilet rules (604.x) - other rules should only
        // show their own entities to avoid misleading screenshots and element highlighting
        involved_db_ids: v.involved_db_ids && v.involved_db_ids.length > 0
          ? v.involved_db_ids
          : (v.entities_used && v.entities_used.length > 0
            ? [...new Set([
                ...((v.regulation_id || '').startsWith('604') ? defaultInvolvedDbIds : []),
                ...v.entities_used.map((e: any) => e.dbId).filter(Boolean),
              ])]
            : defaultInvolvedDbIds),
        confidence: v.confidence || 'medium',
        entities_used: v.entities_used || [],
        icc_status_text: v['ICC A117.1 Status'] || null,
        ada_status_text: v['ADA 2010 Status'] || null,
        tas_status_text: v['TAS 2012 Status'] || null,
        fbc_status_text: v['FBC 2023 Status'] || null,
      }));

      return {
        wc_db_id: rawData.room_summary?.water_closet?.dbId || metadata.room_id,
        room_number: metadata.room_number || null,
        room_name: metadata.room_name || null,
        timestamp: rawData.timestamp || new Date().toISOString(),
        overall_status: overallStatus,
        compliance_score: rawData.compliance_score,
        rule_validations: ruleValidations,
        total_rules: validations.length,
        pass_count: passCount,
        fail_count: failCount,
        warning_count: 0,
        not_tested_count: 0,
        not_applicable_count: 0,
        insufficient_data_count: insufficientCount,
        executive_summary: rawData.executive_summary,
        recommendations: rawData.recommendations || [],
        room_elements: roomElements,
        fixture_elements: fixtureElements,
      };
    }

    // v5 format or already normalized (doors use this format)
    // For doors, use door_db_id as the default involved element
    const defaultInvolvedDbIds = rawData.door_db_id ? [rawData.door_db_id] :
                                  rawData.wc_db_id ? [rawData.wc_db_id] : [];

    // Process rule_validations to add default involved_db_ids if missing
    const processedValidations = (rawData.rule_validations || []).map((v: any) => ({
      ...v,
      involved_db_ids: v.involved_db_ids && v.involved_db_ids.length > 0
        ? v.involved_db_ids
        : defaultInvolvedDbIds,
    }));

    // Room elements for doors - just the door itself
    const roomElements = rawData.room_elements && rawData.room_elements.length > 0
      ? rawData.room_elements
      : defaultInvolvedDbIds;

    return {
      report_type: rawData.report_type || null,
      unit_name: rawData.unit_name || null,
      level: rawData.level || null,
      wc_db_id: rawData.wc_db_id,
      door_db_id: rawData.door_db_id,
      revit_id: rawData.revit_id || null,
      swing_x: rawData.swing_x ?? null,
      swing_y: rawData.swing_y ?? null,
      room_number: rawData.room_number || null,
      room_name: rawData.room_name || null,
      timestamp: rawData.timestamp || new Date().toISOString(),
      overall_status: rawData.overall_status || 'INCONCLUSIVE',
      compliance_score: rawData.compliance_score,
      rule_validations: processedValidations,
      total_rules: rawData.total_rules || 0,
      pass_count: rawData.pass_count || 0,
      fail_count: rawData.fail_count || 0,
      warning_count: rawData.warning_count || 0,
      not_tested_count: rawData.not_tested_count || 0,
      not_applicable_count: rawData.not_applicable_count || 0,
      insufficient_data_count: rawData.insufficient_data_count || 0,
      executive_summary: rawData.executive_summary,
      recommendations: rawData.recommendations || [],
      room_elements: roomElements,
      fixture_elements: rawData.fixture_elements || defaultInvolvedDbIds,
    };
  }

  /**
   * Compress image data to reduce PDF file size
   * Converts to JPEG with quality setting
   */
  private static compressImage(base64Data: string, quality: number = 0.6): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        // Keep reasonable dimensions for good quality screenshots that fill the PDF width
        const maxWidth = 1200;
        const maxHeight = 800;
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        }
        // Convert to JPEG with compression
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve(base64Data); // Return original on error
      img.src = base64Data;
    });
  }

  /**
   * Generate PDF reports from multiple JSON files
   * @param jsonPaths Array of paths to compliance report JSON files
   * @param reportType 'all' for all findings, 'problematic' for failures and warnings only
   * @param onProgress Progress callback
   */
  static async generateFromMultipleJSON(
    jsonPaths: string[],
    reportType: 'all' | 'problematic' = 'problematic',
    onProgress?: (progress: number, message: string) => void,
    projectName?: string,
    scopeLabel?: string,
    defaultLevel?: string,
    jurisdiction?: string,
    modelName?: string,
    regDefFiles?: { ada604?: string; ada404?: string; generic?: string },
    coverLogoPath?: string,
    displayName?: string,
    coverImagePath?: string,
  ): Promise<void> {
    try {
      onProgress?.(0, 'Loading compliance reports...');

      const reportPromises = jsonPaths.map(async (path) => {
        try {
          const response = await fetch(path);
          if (!response.ok) {
            console.warn(`Failed to load ${path}: ${response.statusText}`);
            return [];
          }
          const rawData = await response.json();

          // Cloud scan format (chapters.404.rules) needs expansion into per-door reports
          if (rawData.chapters && rawData.chapters['404']?.rules) {
            const perDoorReports = transformCloudScanToPerDoorReports(rawData);
            return perDoorReports.map((r: any) => this.normalizeReportData(r));
          }

          // Handle array of reports (e.g., Skylight all-units JSON)
          if (Array.isArray(rawData)) {
            return rawData.map((r: any) => this.normalizeReportData(r));
          }
          return [this.normalizeReportData(rawData)];
        } catch (error) {
          console.warn(`Error loading ${path}:`, error);
          return [];
        }
      });

      const reports = (await Promise.all(reportPromises)).flat().filter((r): r is ComplianceReportData => r !== null);

      if (reports.length === 0) {
        throw new Error('No compliance reports could be loaded');
      }

      onProgress?.(5, `Loaded ${reports.length} reports`);

      // Skylight: translate involved_revit_ids → Forge dbIds + family data
      // using the enriched static mapping file
      if (displayName?.toLowerCase() === 'skylight' || jsonPaths.some(p => p.includes('/skylight/'))) {
        try {
          const mapResp = await fetch('/resources/results/skylight/skylight_revit_to_dbid_with_family.json');
          const revitMap: Record<string, { dbId: number; category?: string; familyName?: string; typeName?: string }> = mapResp.ok ? await mapResp.json() : {};
          const mapSize = Object.keys(revitMap).length;
          let translated = 0;

          for (const report of reports as any[]) {
            for (const v of (report.rule_validations || [])) {
              const revitIds: string[] = Array.isArray(v.involved_revit_ids) ? v.involved_revit_ids : [];
              if (revitIds.length > 0 && (!v.involved_db_ids || v.involved_db_ids.length === 0)) {
                const dbIds: number[] = [];
                const families: Array<{ category?: string; familyName?: string; typeName?: string }> = [];
                for (const rid of revitIds) {
                  const entry = revitMap[rid];
                  if (entry?.dbId != null) {
                    dbIds.push(entry.dbId);
                    families.push({ category: entry.category, familyName: entry.familyName, typeName: entry.typeName });
                  }
                }
                if (dbIds.length > 0) {
                  v.involved_db_ids = dbIds;
                  v.involved_families = families;
                  translated++;
                }
              }
            }
          }
          console.log(`[Skylight PDF] Translated ${translated} validations using ${mapSize}-entry mapping file`);
        } catch (err) {
          console.warn('[Skylight PDF] Revit ID mapping load failed:', err);
        }
      }

      await this.generateCombinedPDF(reports, reportType, onProgress, projectName, scopeLabel, defaultLevel, jurisdiction, modelName, regDefFiles, coverLogoPath, displayName, coverImagePath);

    } catch (error) {
      console.error('Error generating combined PDF:', error);
      throw error;
    }
  }

  /**
   * Generate a combined PDF with all reports (optimized for file size)
   * Groups validations by rule_number - one entry per unique rule with list of affected elements
   */
  private static async generateCombinedPDF(
    reports: ComplianceReportData[],
    reportType: 'all' | 'problematic',
    onProgress?: (progress: number, message: string) => void,
    projectName?: string,
    _scopeLabel?: string,
    _defaultLevel?: string,
    jurisdiction?: string,
    modelName?: string,
    regDefFiles?: { ada604?: string; ada404?: string; generic?: string },
    coverLogoPath?: string,
    displayName?: string,
    coverImagePath?: string,
  ): Promise<void> {
    const viewer = (window as any).NOP_VIEWER as Autodesk.Viewing.GuiViewer3D;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    let currentY = margin;
    let isFirstPage = true;

    const addSectionToPDF = async (html: string, forceNewPage: boolean = false): Promise<void> => {
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = `${contentWidth}mm`;
      container.style.backgroundColor = 'white';
      container.innerHTML = `<style>${this.getStyles()}</style>${html}`;
      document.body.appendChild(container);

      const canvas = await html2canvas(container, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(container);

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const maxContentHeight = pageHeight - (margin * 2); // Max height that fits on a page

      // If section doesn't fit on current page, start a new page
      if (forceNewPage || (!isFirstPage && currentY + imgHeight > pageHeight - margin)) {
        pdf.addPage();
        currentY = margin;
      }
      isFirstPage = false;

      const imgData = canvas.toDataURL('image/jpeg', 0.7);

      // If section is taller than a full page, scale it down to fit
      if (imgHeight > maxContentHeight) {
        const scaleFactor = maxContentHeight / imgHeight;
        const scaledWidth = imgWidth * scaleFactor;
        const scaledHeight = maxContentHeight;
        // Center the scaled image horizontally
        const xOffset = margin + (imgWidth - scaledWidth) / 2;
        pdf.addImage(imgData, 'JPEG', xOffset, currentY, scaledWidth, scaledHeight, undefined, 'FAST');
        currentY += scaledHeight + 5;
      } else {
        pdf.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight, undefined, 'FAST');
        currentY += imgHeight + 5;
      }
    };

    // Skylight-only: slice tall sections across multiple pages at full width
    // instead of scaling down (which shrinks content and breaks page consistency).
    const addSectionToPDFSliced = async (html: string, forceNewPage: boolean = false): Promise<void> => {
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = `${contentWidth}mm`;
      container.style.backgroundColor = 'white';
      container.innerHTML = `<style>${this.getStyles()}</style>${html}`;
      document.body.appendChild(container);

      const canvas = await html2canvas(container, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(container);

      const imgWidth2 = contentWidth;
      const imgHeight2 = (canvas.height * imgWidth2) / canvas.width;
      const maxH = pageHeight - (margin * 2);

      if (forceNewPage || (!isFirstPage && currentY + Math.min(imgHeight2, maxH) > pageHeight - margin)) {
        pdf.addPage();
        currentY = margin;
      }
      isFirstPage = false;

      if (imgHeight2 <= maxH) {
        const imgData2 = canvas.toDataURL('image/jpeg', 0.7);
        pdf.addImage(imgData2, 'JPEG', margin, currentY, imgWidth2, imgHeight2, undefined, 'FAST');
        currentY += imgHeight2 + 5;
      } else {
        // Slice the canvas into page-sized chunks
        const pxPerMm = canvas.height / imgHeight2;
        const slicePx = Math.floor(maxH * pxPerMm);
        let srcY = 0;

        while (srcY < canvas.height) {
          const thisPx = Math.min(slicePx, canvas.height - srcY);
          const thisMm = thisPx / pxPerMm;

          const slice = document.createElement('canvas');
          slice.width = canvas.width;
          slice.height = thisPx;
          const ctx = slice.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, slice.width, slice.height);
            ctx.drawImage(canvas, 0, srcY, canvas.width, thisPx, 0, 0, canvas.width, thisPx);
          }

          pdf.addImage(slice.toDataURL('image/jpeg', 0.7), 'JPEG', margin, currentY, imgWidth2, thisMm, undefined, 'FAST');
          srcY += thisPx;
          if (srcY < canvas.height) {
            pdf.addPage();
            currentY = margin;
          } else {
            currentY += thisMm + 5;
          }
        }
      }
    };

    // Group all validations by rule_number across all reports
    interface GroupedValidation {
      rule_number: string;
      rule_name: string;
      rule_raw_text: string;
      status: string;
      llm_reasoning: string | null;
      measured_value: number | string | null;
      required_value: string | null;
      unit: string | null;
      confidence: string;
      // Per-standard requirement values from regulation definitions
      icc_value: string | null;
      tas_value: string | null;
      ada_value: string | null;
      fbc_value: string | null;
      // Per-standard status text from enriched data
      icc_status_text: string | null;
      ada_status_text: string | null;
      tas_status_text: string | null;
      fbc_status_text: string | null;
      // List of affected elements - each with its own analysis
      affectedElements: Array<{
        elementId: number | string;
        revitId: string | null;
        elementType: string;
        roomNumber: string | null;
        roomName: string | null;
        familyName: string | null;
        familyType: string | null;
        measuredValue: number | string | null;
        involved_db_ids: number[];
        room_elements: number[];
        llm_reasoning: string | null; // Per-element analysis
        elementStatus: string; // Per-element status
        swing_x?: number | null;
        swing_y?: number | null;
        entities_used?: Array<{
          dbId: number;
          name?: string;
          role?: string;
          revit_id?: string;
          room_name?: string;
          room_number?: string;
          family_name?: string;
          family_type?: string;
        }>;
      }>;
    }

    const groupedByRule = new Map<string, GroupedValidation>();

    // Helper function to get status priority (higher = worse)
    const getStatusPriority = (status: string): number => {
      const s = status.toUpperCase();
      if (s === 'FAIL' || s === 'FAILED') return 4;
      if (s === 'WARNING') return 3;
      if (s === 'INSUFFICIENT_DATA') return 2;
      if (s === 'NOT_TESTED') return 1;
      return 0; // PASS, NOT_APPLICABLE, etc.
    };

    // Helper function to check if a status is a failure
    const isFailedStatus = (status: string): boolean => {
      const s = status.toUpperCase();
      return s === 'FAIL' || s === 'FAILED';
    };

    // Helper: make "WARNING" (case-insensitive) bold and yellow in analysis text
    const highlightWarning = (text: string): string => {
      return text.replace(/\bWARNING\b/gi, '<strong style="color: #d97706;">WARNING</strong>');
    };

    // Helper function to check if a rule is a door rule (404.x.x)
    const isDoorRule = (ruleNumber: string): boolean => {
      return ruleNumber.startsWith('404');
    };

    // Collect all validations grouped by rule_number
    // Only include elements that have a problematic status for each rule
    reports.forEach(report => {
      const isSkylight = report.report_type === 'skylight_sot';
      const defaultElementId = report.wc_db_id || report.door_db_id || 'Unknown';
      const elementType = isSkylight ? 'Wall' : (report.door_db_id ? 'Door' : 'Toilet');
      const isDoor = !isSkylight && !!report.door_db_id;
      const validations = report.rule_validations || [];

      validations.forEach(v => {
        const key = v.rule_number;

        // Skip INFO/definition-only validations that have no measurement data
        // These come from plumbing_regulations.json and would create empty "Toilet" rows
        const statusUpper2 = (v.status || '').toUpperCase();
        if (statusUpper2 === 'INFO' || statusUpper2 === 'NOT_RUN') {
          return;
        }

        // For non-door rules with entities_used, use the first entity's dbId instead of the toilet
        // This prevents the toilet from appearing as an affected element for rules about mirrors, grab bars, etc.
        // For Skylight: use unit_name as the element identifier
        const elementId = isSkylight
          ? (report.unit_name || report.room_name || 'Unknown')
          : ((!isDoor && v.entities_used && v.entities_used.length > 0 && v.entities_used[0].dbId)
            ? v.entities_used[0].dbId
            : defaultElementId);

        // For "problematic" report type and doors only: filter to show only FAIL/FAILED status
        // Non-door elements (604, 11x, etc.) are not filtered here - they go through normal grouping
        if (reportType === 'problematic' && isDoor && !isFailedStatus(v.status)) {
          return; // Skip this door - it didn't fail
        }

        if (!groupedByRule.has(key)) {
          groupedByRule.set(key, {
            rule_number: v.rule_number,
            rule_name: v.rule_name,
            rule_raw_text: v.rule_raw_text,
            status: v.status,
            llm_reasoning: v.llm_reasoning,
            measured_value: v.measured_value,
            required_value: v.required_value,
            unit: v.unit,
            confidence: v.confidence,
            icc_value: null,
            tas_value: null,
            ada_value: null,
            fbc_value: null,
            icc_status_text: v.icc_status_text || null,
            ada_status_text: v.ada_status_text || null,
            tas_status_text: v.tas_status_text || null,
            fbc_status_text: v.fbc_status_text || null,
            affectedElements: [],
          });
        }

        const group = groupedByRule.get(key)!;

        // Update group status to the worst (highest priority) status encountered
        if (getStatusPriority(v.status) > getStatusPriority(group.status)) {
          group.status = v.status;
          // Also update reasoning/measured values if this is a worse status
          if (v.llm_reasoning) group.llm_reasoning = v.llm_reasoning;
        }

        // Extract enriched data from entities_used (from enriched JSON files)
        const entities = v.entities_used || [];

        // For non-door rules with multiple entities, add one affected element per entity
        const entitiesToAdd = (!isDoor && entities.length > 1) ? entities : [null];
        for (const entityEntry of entitiesToAdd) {
          const currentElementId = entityEntry?.dbId || elementId;
          const currentEntity = entityEntry || entities.find((e: any) => e.dbId === elementId) || entities[0];
          const enrichedRevitId = currentEntity?.revit_id || report.revit_id
            || (isSkylight && (v as any).involved_revit_ids?.length > 0 ? (v as any).involved_revit_ids.join(', ') : null);
          const enrichedRoomName = currentEntity?.room_name || v.room_name || report.room_name;
          const enrichedRoomNumber = currentEntity?.room_number || v.room_number || report.room_number;
          const enrichedFamilyName = currentEntity?.family_name || null;
          const enrichedFamilyType = currentEntity?.family_type || null;

          // Use entity name as element type when available (e.g., "Mirror" instead of "Toilet")
          const effectiveElementType = (!isDoor && currentEntity?.name) ? currentEntity.name : elementType;

          group.affectedElements.push({
            elementId: currentElementId,
            revitId: enrichedRevitId,
            elementType: effectiveElementType,
            roomNumber: enrichedRoomNumber,
            roomName: enrichedRoomName,
            familyName: enrichedFamilyName,
            familyType: enrichedFamilyType,
            measuredValue: v.measured_value,
            involved_db_ids: v.involved_db_ids || [],
            room_elements: report.room_elements || [],
            llm_reasoning: v.llm_reasoning || (isDoor && isFailedStatus(v.status) && v.measured_value
              ? `Measured ${v.measured_value} does not meet requirement: ${v.required_value || 'N/A'}`
              : null),
            elementStatus: v.status,
            swing_x: report.swing_x,
            swing_y: report.swing_y,
            entities_used: entities,
          });
        }
      });
    });

    // Look up Revit IDs from Forge Viewer for elements that don't have one
    const dbIdsNeedingRevitId: number[] = [];
    for (const group of groupedByRule.values()) {
      for (const el of group.affectedElements) {
        if (!el.revitId && typeof el.elementId === 'number') {
          dbIdsNeedingRevitId.push(el.elementId as number);
        }
      }
    }
    if (viewer && dbIdsNeedingRevitId.length > 0) {
      const uniqueDbIds = [...new Set(dbIdsNeedingRevitId)];
      console.log(`[PDF] Looking up Revit IDs for ${uniqueDbIds.length} elements...`);
      const revitIdMap = await new Promise<Map<number, string>>((resolve) => {
        const map = new Map<number, string>();
        viewer.model.getBulkProperties(uniqueDbIds, ['Element ID'], (results: any[]) => {
          for (const r of results) {
            const prop = r.properties.find((p: any) => p.displayName === 'Element ID');
            if (prop) map.set(r.dbId, String(prop.displayValue));
          }
          resolve(map);
        }, () => resolve(new Map()));
      });
      for (const group of groupedByRule.values()) {
        for (const el of group.affectedElements) {
          if (!el.revitId && typeof el.elementId === 'number') {
            el.revitId = revitIdMap.get(el.elementId as number) || null;
          }
        }
      }
      console.log(`[PDF] Looked up Revit IDs for ${revitIdMap.size} elements`);
    }

    // Enrich empty rule_raw_text from regulation definition files
    // Also extract per-standard values (ICC A117.1, TAS 2012, ADA 2010)
    const standardValues = new Map<string, { icc: string | null; tas: string | null; ada: string | null; fbc: string | null }>();
    if (regDefFiles) {
      const definitions = new Map<string, string>();
      console.log('[PDF] Enriching rule_raw_text from regulation definitions:', regDefFiles);

      // Load generic definitions: { "404.2.2": { title, description, "ICC A117.1", "TAS 2012", "ADA 2010" } }
      // or Florida format: { "404.2.3": { title, description, "FBC 2023", "ADA 2010" } }
      if (regDefFiles.generic) {
        try {
          const defUrl = `/resources/${regDefFiles.generic}`;
          console.log('[PDF] Loading generic definitions from:', defUrl);
          const resp = await fetch(defUrl);
          if (resp.ok) {
            const data = await resp.json();
            for (const [key, val] of Object.entries(data) as [string, any][]) {
              if (val.description) definitions.set(key, val.description);
              // Store per-standard values (support both ICC/TAS/ADA and FBC/ADA)
              const icc = val['ICC A117.1'] || null;
              const tas = val['TAS 2012'] || null;
              const ada = val['ADA 2010'] || null;
              const fbc = val['FBC 2023'] || null;
              if (icc || tas || ada || fbc) {
                standardValues.set(key, { icc, tas, ada, fbc });
              }
            }
            console.log(`[PDF] Loaded ${definitions.size} generic definitions, ${standardValues.size} with standard values`);
          } else {
            console.warn('[PDF] Failed to load generic definitions:', resp.status);
          }
        } catch (err) { console.warn('[PDF] Error loading generic definitions:', err); }
      }

      // Load ada604 definitions: { ada604: { "604.2": { title, description } } }
      if (regDefFiles.ada604) {
        try {
          const resp = await fetch(`/resources/${regDefFiles.ada604}`);
          if (resp.ok) {
            const data = await resp.json();
            const entries = data.ada604 || data;
            for (const [key, val] of Object.entries(entries) as [string, any][]) {
              if (val.description) definitions.set(key, val.description);
            }
          }
        } catch { /* ignore */ }
      }

      // Load ada404 definitions: { regulations: [{ ruleNumber, fullText }] }
      if (regDefFiles.ada404) {
        try {
          const resp = await fetch(`/resources/${regDefFiles.ada404}`);
          if (resp.ok) {
            const data = await resp.json();
            if (data.regulations) {
              for (const reg of data.regulations) {
                if (reg.ruleNumber && reg.fullText) {
                  definitions.set(reg.ruleNumber, reg.fullText);
                }
              }
            }
          }
        } catch { /* ignore */ }
      }

      // Enrich rule_raw_text from regulation definition files
      // Always prefer definition file text when available (handles ICC/FBC numbering conflicts)
      let enrichedCount = 0;
      let missingCount = 0;
      for (const group of groupedByRule.values()) {
        // Try exact match first
        let desc = definitions.get(group.rule_number);
        // Fallback: try appending .1 (e.g., 609.4 -> 609.4.1)
        if (!desc) desc = definitions.get(group.rule_number + '.1');
        if (desc) {
          group.rule_raw_text = desc;
          enrichedCount++;
        } else if (!group.rule_raw_text) {
          missingCount++;
          console.warn(`[PDF] No definition found for rule: ${group.rule_number}`);
        }
      }
      console.log(`[PDF] Enriched ${enrichedCount} rules, ${missingCount} still missing definitions`);
    }

    // Apply per-standard values (ICC/TAS/ADA) to grouped validations
    if (standardValues.size > 0) {
      let standardEnriched = 0;
      for (const group of groupedByRule.values()) {
        let vals = standardValues.get(group.rule_number);
        // Fallback: try appending .1
        if (!vals) vals = standardValues.get(group.rule_number + '.1');
        if (vals) {
          group.icc_value = vals.icc;
          group.tas_value = vals.tas;
          group.ada_value = vals.ada;
          group.fbc_value = vals.fbc;
          standardEnriched++;
        }
      }
      console.log(`[PDF] Applied standard values to ${standardEnriched} rules`);
    }

    // Filter based on reportType
    const filteredGroups = Array.from(groupedByRule.values()).filter(g => {
      // Filter out all 604.8.* rules (hidden by request - same as RiskPanel)
      if (g.rule_number.startsWith("604.8")) {
        return false;
      }
      if (reportType === 'all') return true;
      // For problematic report: include FAIL, FAILED, WARNING, INSUFFICIENT_DATA for all rules
      const status = g.status.toUpperCase();
      return status === 'FAIL' || status === 'FAILED' || status === 'WARNING' || status === 'INSUFFICIENT_DATA';
    });

    // Sort: warnings first, then errors (FAIL), then pass
    filteredGroups.sort((a, b) => {
      const aStatus = a.status.toUpperCase();
      const bStatus = b.status.toUpperCase();
      const order = (s: string) => {
        if (s === 'WARNING' || s === 'INSUFFICIENT_DATA' || s === 'NOT_TESTED') return 0;
        if (s === 'FAIL' || s === 'FAILED') return 1;
        return 2;
      };
      return order(aStatus) - order(bStatus);
    });

    // Calculate combined statistics from all reports
    let totalPass = 0;
    let totalFail = 0;
    let totalWarning = 0;
    let totalInsufficientData = 0;
    let totalRules = 0;

    reports.forEach(report => {
      totalPass += report.pass_count || 0;
      totalFail += report.fail_count || 0;
      totalWarning += report.warning_count || 0;
      totalInsufficientData += report.insufficient_data_count || 0;
      totalRules += report.total_rules || 0;
    });

    // ── COVER PAGE ──────────────────────────────────────────────────────
    onProgress?.(8, 'Generating cover page...');

    const displayProjectName = projectName || 'ADA Compliance Project';
    const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Load cover logo if path provided
    let coverLogoDataUrl: string | null = null;
    if (coverLogoPath) {
      try {
        const logoUrl = `/resources/${coverLogoPath}`;
        console.log('[PDF] Loading cover logo from:', logoUrl);
        const logoResp = await fetch(logoUrl);
        if (logoResp.ok) {
          const blob = await logoResp.blob();
          coverLogoDataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          console.log('[PDF] Cover logo loaded successfully');
        }
      } catch (err) { console.warn('[PDF] Error loading cover logo:', err); }
    }

    // Load cover image (BIM model screenshot) if path provided
    let coverImageDataUrl: string | null = null;
    if (coverImagePath) {
      try {
        const imgUrl = `/resources/${coverImagePath}`;
        console.log('[PDF] Loading cover image from:', imgUrl);
        const imgResp = await fetch(imgUrl);
        if (imgResp.ok) {
          const blob = await imgResp.blob();
          coverImageDataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          console.log('[PDF] Cover image loaded successfully');
        }
      } catch (err) { console.warn('[PDF] Error loading cover image:', err); }
    }

    // Compute cover-page stats
    const coverTotalFindings = filteredGroups.length;
    const coverFailCount = filteredGroups.filter(g => {
      const s = g.status.toUpperCase();
      return s === 'FAIL' || s === 'FAILED';
    }).length;
    const coverWarningCount = filteredGroups.filter(g => {
      const s = g.status.toUpperCase();
      return s === 'WARNING' || s === 'INSUFFICIENT_DATA';
    }).length;

    // Count element types per status for findings summary
    const failToiletCount = reports.filter(r => !!r.wc_db_id && (r.rule_validations || []).some(
      (v: any) => { const s = (v.status || '').toUpperCase(); return s === 'FAIL' || s === 'FAILED'; }
    )).length;
    const failDoorCount = reports.filter(r => !!r.door_db_id && (r.rule_validations || []).some(
      (v: any) => { const s = (v.status || '').toUpperCase(); return s === 'FAIL' || s === 'FAILED'; }
    )).length;
    const warnToiletCount = reports.filter(r => !!r.wc_db_id && (r.rule_validations || []).some(
      (v: any) => { const s = (v.status || '').toUpperCase(); return s === 'WARNING' || s === 'INSUFFICIENT_DATA'; }
    )).length;
    const warnDoorCount = reports.filter(r => !!r.door_db_id && (r.rule_validations || []).some(
      (v: any) => { const s = (v.status || '').toUpperCase(); return s === 'WARNING' || s === 'INSUFFICIENT_DATA'; }
    )).length;

    const buildBreakdown = (toilets: number, doors: number): string => {
      const parts: string[] = [];
      if (toilets > 0) parts.push(`${toilets} Toilet Room${toilets > 1 ? 's' : ''}`);
      if (doors > 0) parts.push(`${doors} Door${doors > 1 ? 's' : ''}`);
      return parts.length > 0 ? ` (${parts.join(' · ')})` : '';
    };

    const summaryParts: string[] = [];
    if (coverFailCount > 0) summaryParts.push(`${coverFailCount} FAIL${buildBreakdown(failToiletCount, failDoorCount)}`);
    if (coverWarningCount > 0) summaryParts.push(`${coverWarningCount} WARNING${buildBreakdown(warnToiletCount, warnDoorCount)}`);
    const findingsSummaryText = summaryParts.length > 0
      ? summaryParts.join(' · ')
      : 'No findings';

    // Cover page matching reference PDF style: white background, indigo header bar, info table
    // Uses same color palette as the rest of the report (#4f46e5 indigo, #1e1b4b dark)
    const cellPad = coverImageDataUrl ? '8px 16px' : '12px 16px';
    const coverHtml = `
      <div style="width: 100%; min-height: 1050px; background: #ffffff; color: #1f2937; font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; padding: 0; position: relative;">
        <!-- Header bar -->
        <div style="background: #4f46e5; padding: 14px 32px; display: flex; justify-content: space-between; align-items: center;">
          <div style="font-size: 13px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: white;">HEROCON AI · BIM COMPLIANCE ENGINE</div>
          <div style="font-size: 11px; color: rgba(255,255,255,0.8);">CONFIDENTIAL — Generated by Herocon AI</div>
        </div>

        <!-- Main content area -->
        <div style="padding: ${coverImageDataUrl ? '32px 48px 20px 48px' : '60px 48px 40px 48px'};">
          ${coverLogoDataUrl ? `
          <!-- Client Logo -->
          <div style="margin-bottom: 24px;">
            <img src="${coverLogoDataUrl}" style="max-height: 120px; max-width: 400px;" />
          </div>
          ` : ''}

          <!-- Title -->
          <div style="font-size: 28px; font-weight: 800; color: #4f46e5; margin-bottom: 4px;">${displayName?.toLowerCase() === 'skylight' ? 'Company Standards' : 'ADA Compliance'} — ${displayProjectName}</div>
          <div style="font-size: 32px; font-weight: 300; color: #1e1b4b; margin-bottom: ${coverImageDataUrl ? '12px' : '24px'};">Validation Report</div>

          <!-- Accent line -->
          <div style="width: 100%; height: 4px; background: #4f46e5; border-radius: 2px; margin-bottom: ${coverImageDataUrl ? '20px' : '36px'};"></div>

          <!-- Info table -->
          <table style="width: 100%; border-collapse: collapse; font-size: ${coverImageDataUrl ? '13px' : '14px'}; margin-bottom: ${coverImageDataUrl ? '16px' : '32px'};">
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; width: 140px; vertical-align: top;">Project</td>
              <td style="padding: ${cellPad}; color: #374151;">${displayName || displayProjectName}</td>
            </tr>
            ${modelName ? `<tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; width: 140px; vertical-align: top;">Model</td>
              <td style="padding: ${cellPad}; color: #374151;">${modelName}</td>
            </tr>` : ''}
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; vertical-align: top;">Scope</td>
              <td style="padding: ${cellPad}; color: #374151;">Full Model Scan</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; vertical-align: top;">Report Date</td>
              <td style="padding: ${cellPad}; color: #374151;">${generatedDate}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; vertical-align: top;">State</td>
              <td style="padding: ${cellPad}; color: #374151;">${jurisdiction ? jurisdiction.split('—')[0].trim() : 'N/A'}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; vertical-align: top;">Regulations</td>
              <td style="padding: ${cellPad}; color: #374151;">${jurisdiction && jurisdiction.includes('—') ? jurisdiction.split('—')[1].trim() : (displayName?.toLowerCase() === 'skylight' ? 'Company Standards' : 'ADA 2010')}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; vertical-align: top;">Rules Shown</td>
              <td style="padding: ${cellPad}; color: #374151;">${coverTotalFindings}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: ${cellPad}; font-weight: 700; color: #4f46e5; vertical-align: top;">Findings</td>
              <td style="padding: ${cellPad}; color: #374151;">${findingsSummaryText}</td>
            </tr>
          </table>

          ${coverImageDataUrl ? `
          <!-- BIM Model Image -->
          <div style="margin-top: 8px; text-align: center;">
            <img src="${coverImageDataUrl}" style="max-height: 200px; max-width: 100%; border-radius: 6px; border: 1px solid #e5e7eb; display: block; margin: 0 auto;" />
          </div>
          ` : ''}

          <!-- Powered by -->
          <div style="text-align: center; margin-top: ${coverImageDataUrl ? '16px' : '48px'}; font-size: 12px; color: #9ca3af; font-style: italic;">
            Powered by Herocon AI · herocon.ai
          </div>
        </div>

        <!-- Footer bar -->
        <div style="position: absolute; bottom: 0; left: 0; right: 0; padding: 12px 32px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #9ca3af;">
          <div>${displayProjectName} — ${displayName?.toLowerCase() === 'skylight' ? 'Company Standards' : 'ADA Compliance'} Report  |  Page 1</div>
          <div>Report Date: ${generatedDate}</div>
        </div>
      </div>
    `;
    await addSectionToPDF(coverHtml);

    // ── FINDINGS SUMMARY TABLE (page 2) ──────────────────────────────────
    onProgress?.(9, 'Generating findings summary...');

    const findingsListHtml = filteredGroups.map(group => {
      const statusUpper = group.status.toUpperCase();
      const statusColor = (statusUpper === 'FAIL' || statusUpper === 'FAILED') ? '#dc2626' :
                         statusUpper === 'PASS' ? '#059669' : '#d97706';
      const statusLabel = (statusUpper === 'FAIL' || statusUpper === 'FAILED') ? 'FAIL' :
                         statusUpper === 'PASS' ? 'PASS' : 'WARNING';
      // Apply same element filters as detail pages (e.g., remove Basic Walls from 604.5.1.2.3)
      const summaryElements = group.affectedElements.filter(el => {
        if (group.rule_number === '604.5.1.2.3' && el.elementType.toLowerCase().includes('basic wall')) return false;
        return true;
      });
      const elementCount = summaryElements.length;

      // For Skylight: count warnings vs fails separately within each rule
      let statusDisplay = `<span style="color: ${statusColor}; font-weight: 600;">${statusLabel}</span>`;
      if (displayName?.toLowerCase() === 'skylight') {
        let ruleWarns = 0, ruleFails = 0;
        for (const r of reports as any[]) {
          for (const v of (r.rule_validations || [])) {
            if (v.rule_number !== group.rule_number) continue;
            const vs = (v.status || '').toUpperCase();
            if (vs === 'FAIL' || vs === 'FAILED') ruleFails++;
            else if (vs === 'WARNING') ruleWarns++;
          }
        }
        const parts = [];
        if (ruleFails > 0) parts.push(`<span style="color: #dc2626; font-weight: 600;">${ruleFails} FAIL</span>`);
        if (ruleWarns > 0) parts.push(`<span style="color: #d97706; font-weight: 600;">${ruleWarns} WARNING</span>`);
        statusDisplay = parts.join(' · ') || statusDisplay;
      }

      return `
        <tr>
          <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-weight: 600;">${group.rule_number}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">${group.rule_name}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${elementCount}</td>
          <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">
            ${statusDisplay}
          </td>
        </tr>
      `;
    }).join('');

    // Count total failed and warning findings
    // For Skylight: count per-unit-rule statuses (not just per-rule worst status)
    // so warnings aren't hidden when a rule also has fails in other units.
    let totalFailedFindings = 0;
    let totalWarningFindings = 0;
    if (displayName?.toLowerCase() === 'skylight') {
      // Count individual validations (same method as the per-rule status column)
      for (const r of reports as any[]) {
        for (const v of (r.rule_validations || [])) {
          const s = (v.status || '').toUpperCase();
          if (s === 'FAIL' || s === 'FAILED') totalFailedFindings++;
          else if (s === 'WARNING') totalWarningFindings++;
        }
      }
    } else {
      totalFailedFindings = filteredGroups.filter(g => {
        const s = g.status.toUpperCase();
        return s === 'FAIL' || s === 'FAILED';
      }).length;
      totalWarningFindings = filteredGroups.filter(g => {
        const s = g.status.toUpperCase();
        return s === 'WARNING' || s === 'INSUFFICIENT_DATA' || s === 'NOT_TESTED';
      }).length;
    }

    // Build unit summary table — collect per-unit stats from reports
    const unitStats = new Map<string, { level: string; pass: number; warning: number; fail: number; total: number }>();
    reports.forEach((report: any) => {
      const unitName = report.unit_name || report.room_name || 'Unknown';
      const level = report.level || '';
      if (!unitStats.has(unitName)) {
        unitStats.set(unitName, { level, pass: 0, warning: 0, fail: 0, total: 0 });
      }
      const stats = unitStats.get(unitName)!;
      const validations = report.rule_validations || [];
      // Count unique rules (not individual validation instances)
      // e.g., 4 occurrences of rule 100.1 = 1 unique rule
      const seenRules = new Map<string, string>(); // rule_number → worst status
      validations.forEach((v: any) => {
        const ruleNum = v.rule_number || 'unknown';
        const s = (v.status || '').toUpperCase();
        const prev = seenRules.get(ruleNum);
        // Keep the worst status for each rule
        const priority = (st: string) => st === 'FAIL' || st === 'FAILED' ? 3 : st === 'WARNING' ? 2 : st === 'PASS' ? 1 : 0;
        if (!prev || priority(s) > priority(prev)) {
          seenRules.set(ruleNum, s);
        }
      });
      seenRules.forEach((s) => {
        stats.total++;
        if (s === 'PASS') stats.pass++;
        else if (s === 'FAIL' || s === 'FAILED') stats.fail++;
        else if (s === 'WARNING') stats.warning++;
      });
    });

    const unitSummaryRows = Array.from(unitStats.entries()).map(([name, s]) => `
      <tr>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-weight: 600;">${name}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">${s.level}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${s.total}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;"><span style="color: #d97706; font-weight: 600;">${s.warning}</span></td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;"><span style="color: #dc2626; font-weight: 600;">${s.fail}</span></td>
      </tr>
    `).join('');

    const unitSummaryHtml = unitStats.size > 0 ? `
      <div style="margin-top: 28px;">
        <div style="font-weight: 700; font-size: 13px; margin-bottom: 10px; color: #1f2937;">Scanned Units Summary</div>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background-color: #f3f4f6;">
              <th style="padding: 8px 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Unit</th>
              <th style="padding: 8px 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Level</th>
              <th style="padding: 8px 12px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Rules</th>
              <th style="padding: 8px 12px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Warning</th>
              <th style="padding: 8px 12px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Fail</th>
            </tr>
          </thead>
          <tbody>
            ${unitSummaryRows}
          </tbody>
        </table>
      </div>
    ` : '';

    const findingsSummaryHtml = `
      <div class="header">
        <h1>Findings Summary</h1>
        <div class="header-meta">
          ${reports.length} Elements Analyzed | ${filteredGroups.length} Unique Rules
        </div>
      </div>

      <div style="margin-top: 16px; display: flex; gap: 16px;">
        <div style="padding: 10px 20px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; font-size: 13px;">
          <strong style="color: #dc2626;">Total Failed: ${totalFailedFindings}</strong>
        </div>
        <div style="padding: 10px 20px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 13px;">
          <strong style="color: #d97706;">Total Warnings: ${totalWarningFindings}</strong>
        </div>
      </div>

      <div style="margin-top: 16px;">
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background-color: #f3f4f6;">
              <th style="padding: 10px 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Rule</th>
              <th style="padding: 10px 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Description</th>
              <th style="padding: 10px 12px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Elements</th>
              <th style="padding: 10px 12px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${findingsListHtml}
          </tbody>
        </table>
      </div>

      <div style="margin-top: 28px; padding: 16px 20px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 11px; color: #4b5563;">
        <div style="font-weight: 700; font-size: 12px; margin-bottom: 10px; color: #1f2937;">Status Definitions</div>
        <div style="margin-bottom: 6px;">
          <span style="display: inline-block; width: 10px; height: 10px; background: #dc2626; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>
          <strong style="color: #dc2626;">FAIL</strong> — A code violation has been identified. This element does not meet the applicable regulation requirement.
        </div>
        <div style="margin-bottom: 6px;">
          <span style="display: inline-block; width: 10px; height: 10px; background: #d97706; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>
          <strong style="color: #d97706;">WARNING</strong> — This element is at risk. It is close to a threshold or contains an issue likely to become a site-level mistake if not corrected before construction.
        </div>
        <div style="margin-bottom: 6px;">
          <span style="display: inline-block; width: 10px; height: 10px; background: #059669; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>
          <strong style="color: #059669;">PASS</strong> — This element meets the applicable regulation requirement.
        </div>
      </div>

      ${unitSummaryHtml}
    `;
    await addSectionToPDF(findingsSummaryHtml, true);

    // Resolve Revit IDs to Forge Viewer dbIds for all elements
    // Data may use Revit Element IDs (e.g., 6117198) which differ from Forge viewer dbIds
    // Only resolve IDs that are NOT already valid Forge dbIds in the model
    if (viewer) {
      const revitIdMap = new Map<number, number>(); // revitId -> forgeDbId
      const revitIdsToResolve = new Set<number>();

      // Get the model's instance tree to check if an ID is already a valid Forge dbId
      const instanceTree: any = viewer.model?.getInstanceTree?.();

      for (const group of filteredGroups) {
        for (const el of group.affectedElements) {
          for (const id of el.involved_db_ids) {
            if (typeof id === 'number' && id > 100000) {
              // Check if this ID is already a valid Forge dbId in the model
              // Use !== undefined check: getNodeName returns undefined for invalid dbIds,
              // but can return "" (empty string) for valid nodes without display names
              const nodeName = instanceTree ? instanceTree.getNodeName(id) : undefined;
              const isValidForgeDbId = nodeName !== undefined && nodeName !== null;
              if (isValidForgeDbId) {
                console.log(`[PDF] ID ${id} is already a valid Forge dbId (name: "${nodeName}"), skipping resolution`);
              } else {
                revitIdsToResolve.add(id);
              }
            }
          }
        }
      }

      if (revitIdsToResolve.size > 0) {
        console.log(`[PDF] Resolving ${revitIdsToResolve.size} Revit IDs to Forge viewer dbIds...`);
        for (const revitId of revitIdsToResolve) {
          try {
            const forgeDbId = await PropertiesService.findDbIdByRevitId(viewer, String(revitId));
            if (forgeDbId) {
              revitIdMap.set(revitId, forgeDbId);
            }
          } catch { /* skip */ }
        }
        console.log(`[PDF] Resolved ${revitIdMap.size} of ${revitIdsToResolve.size} Revit IDs`);

        // Replace Revit IDs with Forge dbIds in affected elements
        for (const group of filteredGroups) {
          for (const el of group.affectedElements) {
            el.involved_db_ids = el.involved_db_ids.map(id => revitIdMap.get(id) ?? id);
          }
        }
      }

    }

    // ── Skylight: per-unit rendering ──────────────────────────────────────
    // Instead of one massive rule card with 79 elements across all floors,
    // render one section per unit with its specific findings.
    const isSkylight3 = displayName?.toLowerCase() === 'skylight';
    if (isSkylight3) {
      // 1. Build unit → rules map from the raw reports
      interface UnitRule {
        rule_number: string;
        rule_name: string;
        rule_raw_text: string;
        required_value: string | null;
        status: string;
        validations: Array<{
          level: string;
          measured_value: string;
          llm_reasoning: string;
          room_name: string;
          room_number: string;
          involved_db_ids: number[];
          involved_revit_ids: string[];
          involved_families: Array<{ category?: string; familyName?: string; typeName?: string }>;
        }>;
      }
      const unitMap = new Map<string, { level: string; rules: Map<string, UnitRule> }>();

      for (const report of reports) {
        const unitName = report.unit_name || report.room_name || 'Unknown';
        const level = report.level || '';
        if (!unitMap.has(unitName)) {
          unitMap.set(unitName, { level, rules: new Map() });
        }
        const unit = unitMap.get(unitName)!;
        for (const v of (report.rule_validations || [])) {
          // Group by rule_number + status so WARNING and FAIL get separate cards
          const normStatus = (v.status || '').toUpperCase();
          const statusKey = (normStatus === 'FAIL' || normStatus === 'FAILED') ? 'FAIL' : normStatus;
          const key = `${v.rule_number}::${statusKey}`;
          if (!unit.rules.has(key)) {
            unit.rules.set(key, {
              rule_number: v.rule_number,
              rule_name: v.rule_name,
              rule_raw_text: v.rule_raw_text,
              required_value: v.required_value,
              status: v.status,
              validations: [],
            });
          }
          const rule = unit.rules.get(key)!;
          rule.validations.push({
            level: (v as any).level || '',
            measured_value: String(v.measured_value || ''),
            llm_reasoning: v.llm_reasoning || '',
            room_name: v.room_name || (report as any).room_name || '',
            room_number: v.room_number || '',
            involved_db_ids: v.involved_db_ids || [],
            involved_revit_ids: (v as any).involved_revit_ids || [],
            involved_families: (v as any).involved_families || [],
          });
        }
      }

      // 2. Collect unique rule numbers across all units
      const allRuleNumbers = new Set<string>();
      for (const unitData of unitMap.values()) {
        for (const ruleKey of unitData.rules.keys()) {
          // Key is "rule_number::STATUS" — extract just the rule number
          const ruleNum = ruleKey.split('::')[0];
          allRuleNumbers.add(ruleNum);
        }
      }
      const sortedRuleNumbers = Array.from(allRuleNumbers).sort();

      // Load cover images for Skylight PDFs
      let skylightCoverImgData = '';
      let skylightLogoImgData = '';
      const loadImage = async (path: string): Promise<string> => {
        const resp = await fetch(path);
        if (!resp.ok) return '';
        const blob = await resp.blob();
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      };
      try {
        // Note: the two variables are semantically swapped from their names —
        // `skylightCoverImgData` holds the SKYLIGHT logo (black banner) and
        // `skylightLogoImgData` holds the building 3D render. Kept as-is for
        // compatibility with the existing interpolations below.
        [skylightCoverImgData, skylightLogoImgData] = await Promise.all([
          loadImage('/resources/results/skylight/PHOTO-2026-04-17-13-09-25.jpg'),
          loadImage('/resources/results/skylight/skylight-building-cover.png'),
        ]);
      } catch { /* images not available */ }

      // 3. Generate one PDF per rule number
      const timestamp = new Date().toISOString().split('T')[0];
      let ruleIdx = 0;

      for (const ruleNumber of sortedRuleNumbers) {
        ruleIdx++;
        const progressBase = Math.floor((ruleIdx / sortedRuleNumbers.length) * 90);
        onProgress?.(5 + progressBase, `Generating PDF for rule ${ruleNumber} (${ruleIdx}/${sortedRuleNumbers.length})...`);

        // Get the rule name + rule raw text from the first unit with this rule
        let ruleName = ruleNumber;
        let ruleRawText = '';
        for (const unitData of unitMap.values()) {
          for (const [key, rule] of unitData.rules) {
            if (key.startsWith(ruleNumber + '::')) {
              ruleName = rule.rule_name;
              ruleRawText = (rule as any).rule_raw_text || ruleRawText;
              if (ruleName !== ruleNumber && ruleRawText) break;
            }
          }
          if (ruleName !== ruleNumber && ruleRawText) break;
        }

        // Create a fresh PDF for this rule
        const rulePdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
        let ruleCurrentY = margin;
        let ruleIsFirstPage = true;

        // Helper to add sections to THIS rule's PDF
        // When `fitOnePage` is true, content taller than one page is scaled
        // down to fit instead of being sliced across pages. Used for unit
        // rule cards (each unit = exactly one page) and the findings summary.
        const addToRulePdf = async (html: string, forceNewPage: boolean = false, fitOnePage: boolean = false): Promise<void> => {
          const container = document.createElement('div');
          container.style.position = 'absolute';
          container.style.left = '-9999px';
          container.style.width = `${contentWidth}mm`;
          container.style.backgroundColor = 'white';
          container.innerHTML = `<style>${this.getStyles()}</style>${html}`;
          document.body.appendChild(container);
          const canvas = await html2canvas(container, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff' });
          document.body.removeChild(container);
          let imgW = contentWidth;
          let imgH = (canvas.height * imgW) / canvas.width;
          const maxH = pageHeight - (margin * 2);
          if (forceNewPage || (!ruleIsFirstPage && ruleCurrentY + imgH > pageHeight - margin)) {
            rulePdf.addPage();
            ruleCurrentY = margin;
          }
          ruleIsFirstPage = false;
          if (fitOnePage && imgH > maxH) {
            // Scale down to fit the full page, preserving aspect ratio.
            const scale = maxH / imgH;
            imgH = maxH;
            imgW = imgW * scale;
            const imgData = canvas.toDataURL('image/jpeg', 0.7);
            rulePdf.addImage(imgData, 'JPEG', margin, ruleCurrentY, imgW, imgH, undefined, 'FAST');
            ruleCurrentY += imgH + 5;
          } else if (imgH > maxH) {
            // Slice across pages at full width
            const pxPerMm = canvas.height / imgH;
            const slicePx = Math.floor(maxH * pxPerMm);
            let srcY = 0;
            while (srcY < canvas.height) {
              const thisPx = Math.min(slicePx, canvas.height - srcY);
              const thisMm = thisPx / pxPerMm;
              const slice = document.createElement('canvas');
              slice.width = canvas.width;
              slice.height = thisPx;
              const ctx = slice.getContext('2d');
              if (ctx) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, slice.width, slice.height); ctx.drawImage(canvas, 0, srcY, canvas.width, thisPx, 0, 0, canvas.width, thisPx); }
              rulePdf.addImage(slice.toDataURL('image/jpeg', 0.7), 'JPEG', margin, ruleCurrentY, imgW, thisMm, undefined, 'FAST');
              srcY += thisPx;
              if (srcY < canvas.height) { rulePdf.addPage(); ruleCurrentY = margin; }
              else { ruleCurrentY += thisMm + 5; }
            }
          } else {
            const imgData = canvas.toDataURL('image/jpeg', 0.7);
            rulePdf.addImage(imgData, 'JPEG', margin, ruleCurrentY, imgW, imgH, undefined, 'FAST');
            ruleCurrentY += imgH + 5;
          }
        };

        // ── Cover page for this rule ──
        const coverHtml = `
          <div style="padding: 20px; font-family: 'Segoe UI', system-ui, sans-serif;">
            <div style="background: #4f46e5; color: white; padding: 10px 20px; border-radius: 6px; font-size: 10pt; display: flex; justify-content: space-between; margin-bottom: 20px;">
              <span style="font-weight: 700; letter-spacing: 1px;">HEROCON AI · BIM COMPLIANCE ENGINE</span>
              <span>CONFIDENTIAL</span>
            </div>
            ${skylightCoverImgData ? `
              <div style="margin: 0 auto 20px; text-align: center;">
                <img src="${skylightCoverImgData}" alt="Skylight" style="max-width: 80%; max-height: 110px; object-fit: contain; display: block; margin: 0 auto;" />
              </div>
            ` : ''}
            <div style="font-size: 28px; font-weight: 800; color: #4f46e5; margin-bottom: 4px;">Company Standards — Skylight</div>
            <div style="font-size: 20px; color: #6b7280; margin-bottom: 20px;">Rule ${ruleNumber}: ${ruleName}</div>
            <hr style="border: 2px solid #4f46e5; margin-bottom: 24px;" />
            <table style="width: 70%; font-size: 12pt; border-collapse: collapse;">
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px 16px; font-weight: 700; color: #4f46e5;">Project</td><td style="padding: 10px 16px;">Skylight</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px 16px; font-weight: 700; color: #4f46e5;">Rule</td><td style="padding: 10px 16px;">${ruleNumber}: ${ruleName}</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px 16px; font-weight: 700; color: #4f46e5;">Report Date</td><td style="padding: 10px 16px;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px 16px; font-weight: 700; color: #4f46e5;">State</td><td style="padding: 10px 16px;">${jurisdiction || 'New York'}</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px 16px; font-weight: 700; color: #4f46e5;">Regulations</td><td style="padding: 10px 16px;">Company Standards</td></tr>
              <tr style="border-bottom: 1px solid #e5e7eb;"><td style="padding: 10px 16px; font-weight: 700; color: #4f46e5;">Units Scanned</td><td style="padding: 10px 16px;">${unitMap.size}</td></tr>
            </table>
            <div style="margin-top: 16px; display: flex; gap: 20px; align-items: flex-start;">
              ${skylightLogoImgData ? `
                <div style="flex: 0 0 45%;">
                  <img src="${skylightLogoImgData}" alt="Skylight Project" style="width: 100%; max-height: 320px; object-fit: contain; border-radius: 6px; border: 1px solid #e5e7eb;" />
                </div>
              ` : ''}
              <div style="flex: 1; font-size: 11pt; color: #1f2937; line-height: 1.5;">
                <div style="font-size: 13pt; font-weight: 700; color: #4f46e5; margin-bottom: 8px;">Rule ${ruleNumber}: ${ruleName}</div>
                ${ruleRawText ? `<div style="padding: 12px 14px; background: #f8f9fc; border-left: 4px solid #4f46e5; border-radius: 4px;">${ruleRawText}</div>` : ''}
              </div>
            </div>
            <div style="margin-top: 24px; text-align: center; color: #9ca3af; font-size: 10pt; font-style: italic;">Powered by Herocon AI · herocon.ai</div>
          </div>
        `;
        await addToRulePdf(coverHtml, false);

        // ── Findings summary (page 2), scoped to THIS rule only ──
        // Rule table, totals, and Scanned Units Summary all reflect only the
        // rule this PDF covers.
        const currentGroup = filteredGroups.find(g => g.rule_number === ruleNumber);
        let ruleFailCount = 0;
        let ruleWarnCount = 0;
        for (const r of reports as any[]) {
          for (const v of (r.rule_validations || [])) {
            if (v.rule_number !== ruleNumber) continue;
            const vs = (v.status || '').toUpperCase();
            if (vs === 'FAIL' || vs === 'FAILED') ruleFailCount++;
            else if (vs === 'WARNING') ruleWarnCount++;
          }
        }
        // Rule-scoped Scanned Units Summary: one row per unit that has at
        // least one validation for this rule. Rules column is always 1.
        // Warning/Fail columns count this rule's validations per unit.
        interface RuleUnitStat { level: string; warning: number; fail: number; }
        const ruleUnitStats = new Map<string, RuleUnitStat>();
        for (const r of reports as any[]) {
          const unitName = r.unit_name || r.room_name || 'Unknown';
          const level = r.level || '';
          let hasThisRule = false;
          let uWarn = 0;
          let uFail = 0;
          for (const v of (r.rule_validations || [])) {
            if (v.rule_number !== ruleNumber) continue;
            hasThisRule = true;
            const vs = (v.status || '').toUpperCase();
            if (vs === 'FAIL' || vs === 'FAILED') uFail++;
            else if (vs === 'WARNING') uWarn++;
          }
          if (!hasThisRule) continue;
          ruleUnitStats.set(unitName, { level, warning: uWarn, fail: uFail });
        }
        const ruleUnitSummaryRows = Array.from(ruleUnitStats.entries()).map(([name, s]) => `
          <tr>
            <td style="padding: 4px 10px; border-bottom: 1px solid #e5e7eb; font-weight: 600;">${name}</td>
            <td style="padding: 4px 10px; border-bottom: 1px solid #e5e7eb;">${s.level}</td>
            <td style="padding: 4px 10px; border-bottom: 1px solid #e5e7eb; text-align: center;">1</td>
            <td style="padding: 4px 10px; border-bottom: 1px solid #e5e7eb; text-align: center;"><span style="color: #d97706; font-weight: 600;">${s.warning}</span></td>
            <td style="padding: 4px 10px; border-bottom: 1px solid #e5e7eb; text-align: center;"><span style="color: #dc2626; font-weight: 600;">${s.fail}</span></td>
          </tr>
        `).join('');
        const ruleUnitSummaryHtml = ruleUnitStats.size > 0 ? `
          <div style="margin-top: 14px;">
            <div style="font-weight: 700; font-size: 10px; margin-bottom: 6px; color: #1f2937;">Scanned Units Summary</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
              <thead>
                <tr style="background-color: #f3f4f6;">
                  <th style="padding: 5px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Unit</th>
                  <th style="padding: 5px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Level</th>
                  <th style="padding: 5px 10px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Rules</th>
                  <th style="padding: 5px 10px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Warning</th>
                  <th style="padding: 5px 10px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Fail</th>
                </tr>
              </thead>
              <tbody>${ruleUnitSummaryRows}</tbody>
            </table>
          </div>
        ` : '';

        let ruleRowHtml = '';
        if (currentGroup) {
          const statusParts: string[] = [];
          if (ruleFailCount > 0) statusParts.push(`<span style="color: #dc2626; font-weight: 600;">${ruleFailCount} FAIL</span>`);
          if (ruleWarnCount > 0) statusParts.push(`<span style="color: #d97706; font-weight: 600;">${ruleWarnCount} WARNING</span>`);
          const statusDisplay = statusParts.join(' · ') || '-';
          const summaryElements = currentGroup.affectedElements.filter(el => {
            if (currentGroup.rule_number === '604.5.1.2.3' && el.elementType.toLowerCase().includes('basic wall')) return false;
            return true;
          });
          ruleRowHtml = `
            <tr>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-weight: 600;">${currentGroup.rule_number}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">${currentGroup.rule_name}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${summaryElements.length}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${statusDisplay}</td>
            </tr>
          `;
        }
        const ruleFindingsSummaryHtml = `
          <div class="header" style="margin-bottom: 12px; padding-bottom: 8px;">
            <h1 style="font-size: 18pt; margin-bottom: 4px;">Findings Summary</h1>
            <div class="header-meta">
              ${reports.length} Elements Analyzed | 1 Unique Rule
            </div>
          </div>

          <div style="margin-top: 10px; display: flex; gap: 10px;">
            <div style="padding: 6px 14px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; font-size: 10px;">
              <strong style="color: #dc2626;">Total Failed: ${ruleFailCount}</strong>
            </div>
            <div style="padding: 6px 14px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 10px;">
              <strong style="color: #d97706;">Total Warnings: ${ruleWarnCount}</strong>
            </div>
          </div>

          <div style="margin-top: 10px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
              <thead>
                <tr style="background-color: #f3f4f6;">
                  <th style="padding: 6px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Rule</th>
                  <th style="padding: 6px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Description</th>
                  <th style="padding: 6px 10px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Elements</th>
                  <th style="padding: 6px 10px; text-align: center; font-weight: 600; border-bottom: 2px solid #d1d5db;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${ruleRowHtml}
              </tbody>
            </table>
          </div>

          <div style="margin-top: 14px; padding: 10px 14px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 9px; color: #4b5563;">
            <div style="font-weight: 700; font-size: 10px; margin-bottom: 6px; color: #1f2937;">Status Definitions</div>
            <div style="margin-bottom: 4px;">
              <span style="display: inline-block; width: 8px; height: 8px; background: #dc2626; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>
              <strong style="color: #dc2626;">FAIL</strong> — A code violation has been identified. This element does not meet the applicable regulation requirement.
            </div>
            <div style="margin-bottom: 4px;">
              <span style="display: inline-block; width: 8px; height: 8px; background: #d97706; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>
              <strong style="color: #d97706;">WARNING</strong> — This element is at risk. It is close to a threshold or contains an issue likely to become a site-level mistake if not corrected before construction.
            </div>
            <div style="margin-bottom: 4px;">
              <span style="display: inline-block; width: 8px; height: 8px; background: #059669; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>
              <strong style="color: #059669;">PASS</strong> — This element meets the applicable regulation requirement.
            </div>
          </div>

          ${ruleUnitSummaryHtml}
        `;
        await addToRulePdf(ruleFindingsSummaryHtml, true, true);

        // ── Per-unit findings for this rule ──
        let unitCount = 0;
        for (const [unitName, unitData] of unitMap) {
          // Find rules matching this rule number (may have WARNING + FAIL as separate entries)
          const matchingRules: UnitRule[] = [];
          for (const [key, rule] of unitData.rules) {
            if (key.startsWith(ruleNumber + '::')) matchingRules.push(rule);
          }
          if (matchingRules.length === 0) continue;
          unitCount++;

          for (const rule of matchingRules) {
            const statusUp = rule.status.toUpperCase();
            const isFail = statusUp === 'FAIL' || statusUp === 'FAILED';
            const statusLabel = isFail ? 'FAIL' : statusUp === 'WARNING' ? 'WARNING' : 'PASS';
            const statusColor = isFail ? '#dc2626' : statusUp === 'WARNING' ? '#d97706' : '#059669';
            const tableHeaderBg = '#1e3a5f';
            const tableHeaderColor = '#ffffff';
            const statusBadgeBg = isFail ? '#fef2f2' : statusUp === 'WARNING' ? '#fffbeb' : '#f0fdf4';
            const statusBadgeBorder = isFail ? '#fecaca' : statusUp === 'WARNING' ? '#fde68a' : '#bbf7d0';

            const elemRows = rule.validations.map(v => {
              const locParts = [];
              if (v.level) locParts.push(v.level);
              if (v.room_number) locParts.push(`Room ${v.room_number}`);
              if (v.room_name && v.room_name !== unitName) locParts.push(v.room_name);
              const loc = locParts.join(' — ') || '-';
              const measured = v.measured_value
                ? `<span style="color: ${statusColor}; font-weight: 600;">${v.measured_value}</span>`
                : '-';
              // Build element info from family data
              const elemParts = v.involved_revit_ids.map((rid: string, i: number) => {
                const dbId = v.involved_db_ids[i] ?? '—';
                const fam = v.involved_families[i];
                const famName = fam?.familyName || 'Wall';
                const typeName = fam?.typeName || '';
                const category = fam?.category?.replace('Revit ', '') || '';
                return `
                  <div style="margin-bottom: 4px;">
                    <strong>${famName}</strong>${typeName ? `<span style="color: #6b7280; font-size: 8pt;"> — ${typeName}</span>` : ''}
                    <div style="font-size: 7pt; color: #9ca3af;">${category ? category + ' · ' : ''}Revit ${rid} · dbId ${dbId}</div>
                  </div>`;
              });
              const elemHtml = elemParts.length > 0 ? elemParts.join('') : '<strong>Wall</strong>';
              return `<tr>
                <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 9pt;">${elemHtml}</td>
                <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #6b7280;">${loc}</td>
                <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${measured}</td>
              </tr>`;
            }).join('');

            // Combine distinct llm_reasoning entries, preserving every measured value.
            // When a unit has multiple validations for the same rule (e.g. two FAILs at
            // 3'-6" and 3'-7 1/4"), we dedupe by reasoning text and aggregate the
            // measured values under each unique reasoning so readers see both findings.
            const distinctReasonings = new Map<string, { reasoning: string; labels: string[] }>();
            for (const v of rule.validations) {
              const r = (v.llm_reasoning || '').trim();
              if (!r) continue;
              const key = r.toLowerCase();
              const label = String(v.measured_value || '').trim();
              const existing = distinctReasonings.get(key);
              if (existing) {
                if (label && !existing.labels.includes(label)) existing.labels.push(label);
              } else {
                distinctReasonings.set(key, { reasoning: r, labels: label ? [label] : [] });
              }
            }
            let analysisInner: string;
            if (distinctReasonings.size === 0) {
              analysisInner = `<strong>Analysis:</strong> ${rule.rule_name}`;
            } else if (distinctReasonings.size === 1) {
              const entry = Array.from(distinctReasonings.values())[0];
              const prefix = entry.labels.length > 0 ? `<strong>${entry.labels.join(', ')}:</strong> ` : '';
              analysisInner = `<strong>Analysis:</strong> ${prefix}${entry.reasoning}`;
            } else {
              const items = Array.from(distinctReasonings.values()).map(entry => {
                const prefix = entry.labels.length > 0 ? `<strong>${entry.labels.join(', ')}:</strong> ` : '';
                return `<li style="margin-bottom: 4px;">${prefix}${entry.reasoning}</li>`;
              }).join('');
              analysisInner = `<strong>Analysis:</strong><ul style="margin: 6px 0 0 18px; padding: 0;">${items}</ul>`;
            }

            // Aggregate every unique dbId across all validations for this unit+rule.
            // A single validation row can involve multiple elements (e.g. a Low Boy
            // Encloser paired with a Basic Wall); the screenshot must isolate all
            // of them together, and the header count must reflect the total.
            const uniqueDbIds = new Set<number>();
            for (const v of rule.validations) {
              for (const id of v.involved_db_ids) {
                if (typeof id === 'number' && Number.isFinite(id)) uniqueDbIds.add(id);
              }
            }
            const allDbIds = Array.from(uniqueDbIds);
            const affectedElementCount = allDbIds.length || rule.validations.length;

            // Screenshot: all Skylight rules route through captureSkylightScreenshot,
            // which colors involved elements by status, auto-detects host
            // walls for door rules (100.2) and colors them cyan, and
            // includes every leaf in the visible area (not just walls).
            const isSkyDoorRule = /^100\.2(\.|$)/.test(rule.rule_number);
            let screenshotHtml = '';
            if (viewer && allDbIds.length > 0) {
              const looksBlank = (d: string | null): boolean =>
                !d || !d.startsWith('data:image/') || d.length < 2 * 1024;

              const captureSkylight = async (): Promise<string | null> => {
                try {
                  const ssPromise = this.captureSkylightScreenshot(viewer, allDbIds, rule.status, isSkyDoorRule);
                  const timeoutP = new Promise<string>((_, rej) => setTimeout(() => rej(new Error('timeout')), 12000));
                  const raw = await Promise.race([ssPromise, timeoutP]);
                  return await this.compressImage(raw, 0.5);
                } catch (err) {
                  console.warn(`[PDF] Skylight screenshot failed for ${unitName} ${rule.rule_number}:`, err);
                  return null;
                }
              };
              const captureFallback = async (skipFloorBand: boolean, skipNearby: boolean): Promise<string | null> => {
                try {
                  const ssPromise = this.captureViewerScreenshot(
                    viewer, allDbIds, [], rule.status, false, null, null, skipFloorBand, skipNearby,
                  );
                  const timeoutP = new Promise<string>((_, rej) => setTimeout(() => rej(new Error('timeout')), 10000));
                  const rawSS = await Promise.race([ssPromise, timeoutP]);
                  return await this.compressImage(rawSS, 0.5);
                } catch (err) {
                  console.warn(`[PDF] Fallback screenshot failed (skipFloorBand=${skipFloorBand}, skipNearby=${skipNearby}) for ${unitName} ${rule.rule_number}:`, err);
                  return null;
                }
              };

              // Door rules use the new per-door-host helper.
              // Non-door rules (corridors, bedrooms) stay on the proven
              // captureViewerScreenshot path which reliably renders scenes
              // with surrounding context — prevents the blank-frame
              // regressions the new path has for some multi-floor units.
              let ssData: string | null;
              if (isSkyDoorRule) {
                ssData = await captureSkylight();
                if (looksBlank(ssData)) ssData = await captureFallback(true, false);
                if (looksBlank(ssData)) ssData = await captureFallback(true, true);
              } else {
                ssData = await captureFallback(false, false);
                if (looksBlank(ssData)) ssData = await captureFallback(true, false);
                if (looksBlank(ssData)) ssData = await captureFallback(true, true);
              }
              if (ssData && ssData.startsWith('data:image/')) {
                screenshotHtml = `
                  <div class="screenshot-container" style="page-break-inside: avoid;">
                    <div style="font-size: 12pt; font-weight: 700; color: #1f2937; margin-bottom: 8px; text-align: center; letter-spacing: 0.3px;">3D viewer screenshot of detected issues</div>
                    <img src="${ssData}" alt="Screenshot for ${unitName} ${rule.rule_number}" class="screenshot-image" style="max-height: 200px;" />
                  </div>`;
              }
            }

            const ruleCardHtml = `
              <div class="rule-card" style="border-left: 4px solid ${statusColor}; background: ${isFail ? '#fef2f2' : statusUp === 'WARNING' ? '#fffdf5' : '#f0fdf4'};">
                <div style="font-size: 14pt; color: #1f2937; font-weight: 700; text-align: center; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;">${unitName}</div>
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px;">
                  <div>
                    <div style="font-size: 10pt; color: ${statusColor}; font-weight: 600;">Company Standards — ${rule.rule_number}</div>
                    <div style="font-size: 14pt; font-weight: 700; color: #1f2937;">${rule.rule_name}</div>
                  </div>
                  <span style="background: ${statusBadgeBg}; color: ${statusColor}; font-weight: 700; padding: 6px 16px; border-radius: 6px; font-size: 11pt; border: 1px solid ${statusBadgeBorder};">${statusLabel}</span>
                </div>
                <div style="font-size: 9pt; color: #6b7280; margin-bottom: 6px;">
                  <strong style="color: ${statusColor};">Location:</strong> ${unitName}${unitData.level ? ' — ' + unitData.level : ''}
                </div>
                <div style="background: ${isFail ? '#fef2f2' : '#fffbeb'}; border: 1px solid ${isFail ? '#fecaca' : '#fde68a'}; border-radius: 6px; padding: 10px 14px; margin-bottom: 10px; font-size: 9pt;">
                  ${analysisInner}
                </div>
                <div style="border-left: 3px solid #d1d5db; padding-left: 12px; margin-bottom: 12px; font-size: 9pt; color: #4b5563;">${rule.rule_raw_text || ''}</div>
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px; border: 1px solid ${tableHeaderBg};">
                  <thead>
                    <tr><th colspan="2" style="background: ${tableHeaderBg}; color: ${tableHeaderColor}; padding: 8px 12px; font-size: 10pt; font-weight: 700; text-align: center; border-bottom: 1px solid rgba(255,255,255,0.3);">Company Standards</th></tr>
                    <tr style="background: ${tableHeaderBg};">
                      <th style="color: ${tableHeaderColor}; padding: 6px 12px; font-size: 9pt; font-weight: 600; text-align: center; width: 40%; border-right: 1px solid rgba(255,255,255,0.3);">Rule</th>
                      <th style="color: ${tableHeaderColor}; padding: 6px 12px; font-size: 9pt; font-weight: 600; text-align: center;">Requirement</th>
                    </tr>
                  </thead>
                  <tbody><tr>
                    <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb; font-weight: 600; text-align: center;">${rule.rule_number}</td>
                    <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${rule.required_value || '-'}</td>
                  </tr></tbody>
                </table>
                <div style="font-weight: 700; font-size: 10pt; margin-bottom: 6px;">Affected Elements (${affectedElementCount}):</div>
                <table style="width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 14px;">
                  <thead><tr style="background: #f3f4f6;">
                    <th style="padding: 8px 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Element</th>
                    <th style="padding: 8px 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #d1d5db;">Location</th>
                    <th style="padding: 8px 12px; text-align: right; font-weight: 600; border-bottom: 2px solid #d1d5db;">Measured</th>
                  </tr></thead>
                  <tbody>${elemRows}</tbody>
                </table>
                ${screenshotHtml}
              </div>
            `;

            await addToRulePdf(ruleCardHtml, true, true);
          }
        }

        // Save this rule's PDF
        // Download using blob URL + anchor click — more reliable than pdf.save()
        // for multiple sequential downloads
        const pdfBlob = rulePdf.output('blob');
        const blobUrl = URL.createObjectURL(pdfBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = `Skylight-Rule-${ruleNumber}-${ruleName.replace(/\s+/g, '_')}-${timestamp}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
        // Small pause to let the browser process the download before the next one
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      onProgress?.(98, 'All PDFs generated');
      // Don't save the main pdf — we saved separate ones
      return;
    }

    // ── Standard (non-Skylight): per-rule rendering ──────────────────────
    // Process each grouped validation - one screenshot per rule
    let ruleIndex = 0;
    for (const group of filteredGroups) {
      ruleIndex++;
      const progressBase = 10 + Math.floor((ruleIndex / filteredGroups.length) * 85);

      onProgress?.(progressBase, `Processing rule ${group.rule_number} (${ruleIndex}/${filteredGroups.length})...`);

      // Check if this is a door rule (404.x) - doors get different table format
      const isGroupDoorRule = isDoorRule(group.rule_number);

      // Screenshot uses the FIRST affected element only — a representative example.
      // Using ALL elements would span the entire building (elements across 20+ floors)
      // causing fitToView to zoom out to show the whole building.
      let screenshotData: string | undefined;
      const firstElement = group.affectedElements[0];

      if (viewer && firstElement && firstElement.involved_db_ids.length > 0) {
        console.log(`[PDF] Screenshot for ${group.rule_number}: involved_db_ids =`, firstElement.involved_db_ids);
        try {
          // Wrap screenshot capture with a timeout to prevent hanging on invalid element IDs
          const screenshotPromise = this.captureViewerScreenshot(
            viewer,
            firstElement.involved_db_ids,
            firstElement.room_elements,
            group.status,
            isGroupDoorRule,
            firstElement?.swing_x ?? null,
            firstElement?.swing_y ?? null,
          );
          const timeoutPromise = new Promise<string>((_, reject) =>
            setTimeout(() => reject(new Error('Screenshot capture timed out')), 10000)
          );
          const rawScreenshot = await Promise.race([screenshotPromise, timeoutPromise]);
          screenshotData = await this.compressImage(rawScreenshot, 0.5);
        } catch (error) {
          console.warn(`Failed to capture screenshot for rule ${group.rule_number}:`, error);
        }
      }

      // Generate HTML for grouped validation with list of affected elements
      const statusUpper = group.status.toUpperCase();
      const cardType = (statusUpper === 'FAIL' || statusUpper === 'FAILED') ? 'fail' :
                      statusUpper === 'PASS' ? 'pass' : 'warning';

      // Analysis text overrides for specific rules (shown in PDF only)
      const analysisOverrides: Record<string, string> = {
        '404.2.4': 'Door 6117198 (36x84, Production) fails on the pull side: parallel clearance measured 14.18 inches, below the 18 inch minimum. Push side passes at 52.41 x 13.96 inches.\n\nDoor 11203915 (36x84, Corridor) passes on both sides: pull 93.81 x 25.03 inches, push 95.81 x 53.61 inches.\n\nDoor 5514121 (42x84, Production) passes on both sides: pull 120 x 120 inches, push 120 x 15.56 inches.',
        '404.2.4.1': 'Thresholds (Ref. Sections 302 & 303): If provided, thresholds at doorways shall be \u00bd inch (13 mm) maximum in height. Raised thresholds and changes in level at doorways shall comply with Sections 302 and 303.\n\nException: An existing or altered threshold shall be permitted to be \u00be inch (19 mm) maximum in height, provided the threshold has a beveled edge on each side with a maximum slope of 1:2 for the height exceeding \u00bc inch (6.4 mm).',
        '404.2.10': 'Vision lite bottom edge was measured at 43.5 inches above the finish floor. The maximum allowed height is 43 inches (1090 mm). Both doors in the Production area (36x84 and 42x84 Single-Flush) exceed the limit by 0.5 inches. The exception for vision lites above 66 inches does not apply here.',
        '404.2.11': 'Vision lite bottom edge was measured at 43.5 inches above the finish floor. The maximum allowed height is 43 inches (1090 mm). Both doors in the Production area (36x84 and 42x84 Single-Flush) exceed the limit by 0.5 inches. The exception for vision lites above 66 inches does not apply here.',
      };
      // Additional text appended to existing analysis (not replacing it)
      const analysisAppends: Record<string, string> = {
        '609.4': 'The rule states to measure from the top of the grip.',
      };
      // Always show analysis at the top (between location and rule text) for consistency
      const showTopAnalysis = true;

      // Build table rows for affected elements
      // Doors: show Status column + per-element analysis
      // Toilets/Others: original format - no Status column, single analysis at bottom
      // Collect all element IDs that have their own row, to avoid duplicating them as "Involved element"
      // Filter affected elements for specific rules:
      // 604.5.1.2.3: remove "Basic Wall" elements (not relevant to grab bar rule)
      const filteredAffectedElements = group.affectedElements.filter(el => {
        if (group.rule_number === '604.5.1.2.3' && el.elementType.toLowerCase().includes('basic wall')) return false;
        return true;
      });

      const allAffectedIds = new Set(filteredAffectedElements.map(e => Number(e.elementId)));
      const affectedElementsRowsArr: string[] = [];
      for (const el of filteredAffectedElements) {
        // Build location string: Room {number} - {name}
        let location = '';
        if (el.roomNumber || el.roomName) {
          const parts = [];
          if (el.roomNumber) parts.push(`Room ${el.roomNumber}`);
          if (el.roomName) parts.push(el.roomName);
          location = parts.join(' - ');
        }
        // Format measured value with color if it exists
        const measuredHtml = el.measuredValue !== null && el.measuredValue !== undefined
          ? `<span style="color: #dc2626; font-weight: 600;">${el.measuredValue}</span>`
          : '-';
        // Use appropriate icon based on element type
        const icon = el.elementType.toLowerCase().includes('door') ? '🚪'
          : el.elementType === 'Wall' ? '🧱' : '🚽';

        if (isGroupDoorRule) {
          // Door format: includes Status column and per-element analysis
          const elStatusUpper = el.elementStatus.toUpperCase();
          const statusColor = (elStatusUpper === 'FAIL' || elStatusUpper === 'FAILED') ? '#dc2626' :
                             elStatusUpper === 'PASS' ? '#059669' : '#d97706';

          const revitIdDisplay = el.revitId || 'N/A';
          const idsHtml = `<div style="font-size: 8pt; color: #6b7280; margin-top: 2px;">Revit ID: ${revitIdDisplay} &nbsp;|&nbsp; dbId: ${el.elementId}</div>`;
          const familyHtml = el.familyName ? `<div style="font-size: 8pt; color: #6b7280;">Family: ${el.familyName}${el.familyType && el.familyType !== 'Not a Type-See Type Catalog' ? ` — ${el.familyType}` : ''}</div>` : '';
          let rowHtml = `<tr>
            <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">
              <span style="margin-right: 6px;">${icon}</span>
              <strong>${el.elementType} ${el.elementId}</strong>
              ${idsHtml}
              ${familyHtml}
            </td>
            <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #6b7280;">${location || '-'}</td>
            <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">
              <span style="color: ${statusColor}; font-weight: 600;">${el.elementStatus}</span>
            </td>
            <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${measuredHtml}</td>
          </tr>`;

          // Add rows for additional involved elements (e.g., bollard cover obstructing clearance)
          // Exclude bollard (dbId 59656) from 404.2.10/404.2.11 table — kept in screenshot only
          // 404.2.3.2: skip all additional involved elements — only the main door entries matter
          if (group.rule_number !== '404.2.3.2') {
          const additionalIds = el.involved_db_ids.filter(id =>
            id !== el.elementId && id !== Number(el.elementId) &&
            !allAffectedIds.has(id) &&
            !((group.rule_number === '404.2.10' || group.rule_number === '404.2.11') && id === 59656)
          );
          for (const id of additionalIds) {
            const instanceTree2: any = viewer?.model?.getInstanceTree?.();
            const nodeName = instanceTree2 ? instanceTree2.getNodeName(id) : null;
            // Extract Revit ID from node name pattern "TypeName [RevitId]"
            const revitMatch = nodeName?.match(/\[(\d+)\]/);
            const entRevitId = revitMatch ? revitMatch[1] : String(id);
            // Clean display name: remove the [RevitId] suffix
            const cleanName = nodeName ? nodeName.replace(/\s*\[\d+\]$/, '') : `Element ${id}`;
            rowHtml += `<tr>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">
                <span style="margin-right: 6px;">📦</span>
                <strong>${cleanName}</strong>
                <div style="font-size: 8pt; color: #6b7280; margin-top: 2px;">Revit ID: ${entRevitId} &nbsp;|&nbsp; dbId: ${id}</div>
                <div style="font-size: 8pt; color: #9ca3af;">Involved element</div>
              </td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #6b7280;">-</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">
                <span style="color: #6b7280;">-</span>
              </td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">-</td>
            </tr>`;
          }
          } // end 404.2.3.2 guard

          // Add analysis row for doors (skip if analysis is shown at top)
          if (el.llm_reasoning && !showTopAnalysis) {
            rowHtml += `<tr>
              <td colspan="4" style="padding: 8px 12px 12px 40px; border-bottom: 1px solid #e5e7eb; background: #f8fafc;">
                <div style="font-size: 9pt; color: #4b5563;">
                  <strong style="color: #374151;">Analysis:</strong> ${highlightWarning(el.llm_reasoning)}
                </div>
              </td>
            </tr>`;
          }

          affectedElementsRowsArr.push(rowHtml);
        } else {
          // Toilet/Other format: one row per involved entity
          // Parse measured value to extract per-entity numbers
          const measuredStr = String(el.measuredValue || '');

          // Helper: extract just numbers+inch for a given dbId or name from measured string
          const extractMeasuredForEntity = (dbId: number, name?: string): string => {
            // Try to find pattern like "Name (dbId): 35.62"" or "(dbId): 35.62""
            const dbIdPattern = new RegExp(`\\(${dbId}\\):\\s*([\\d.]+)"?`);
            const dbIdMatch = measuredStr.match(dbIdPattern);
            if (dbIdMatch) return `${dbIdMatch[1]}"`;

            // Try name-based: "Side wall (82063): 35.62""
            if (name) {
              const namePattern = new RegExp(`${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^:]*:\\s*([\\d.]+)"?`, 'i');
              const nameMatch = measuredStr.match(namePattern);
              if (nameMatch) return `${nameMatch[1]}"`;
            }

            // If measured value is just a plain number, use it as-is
            const plainNum = measuredStr.match(/^([\d.]+)$/);
            if (plainNum) return `${plainNum[1]}"`;

            // Fallback: return the full measured string rather than '-'
            return measuredStr || '-';
          };

          {
            const revitIdDisplay2 = el.revitId || 'N/A';
            const familyDisplay = el.familyName || el.elementType;
            const familyTypeDisplay = el.familyType && el.familyType !== 'Not a Type-See Type Catalog' ? ` — ${el.familyType}` : '';
            const entMeasured = extractMeasuredForEntity(Number(el.elementId), el.elementType);

            affectedElementsRowsArr.push(`<tr>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb;">
                <strong>${familyDisplay}${familyTypeDisplay}</strong>
                <div style="font-size: 8pt; color: #6b7280; margin-top: 2px;">Revit ID: ${revitIdDisplay2} &nbsp;|&nbsp; dbId: ${el.elementId}</div>
              </td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #6b7280;">${location || '-'}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">
                <span style="color: #dc2626; font-weight: 600;">${entMeasured}</span>
              </td>
            </tr>`);
          }
        }
      }
      const affectedElementsRows = affectedElementsRowsArr.join('');

      // Different table headers for doors vs toilets
      const affectedElementsHtml = isGroupDoorRule ? `
        <table style="width: 100%; border-collapse: collapse; font-size: 10pt;">
          <thead>
            <tr style="background: #f9fafb;">
              <th style="padding: 8px 12px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Element</th>
              <th style="padding: 8px 12px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Location</th>
              <th style="padding: 8px 12px; text-align: center; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Status</th>
              <th style="padding: 8px 12px; text-align: right; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Measured</th>
            </tr>
          </thead>
          <tbody>
            ${affectedElementsRows}
          </tbody>
        </table>
      ` : `
        <table style="width: 100%; border-collapse: collapse; font-size: 10pt;">
          <thead>
            <tr style="background: #f9fafb;">
              <th style="padding: 8px 12px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Element</th>
              <th style="padding: 8px 12px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Location</th>
              <th style="padding: 8px 12px; text-align: right; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb;">Measured</th>
            </tr>
          </thead>
          <tbody>
            ${affectedElementsRows}
          </tbody>
        </table>
      `;

      const baseAnalysis = analysisOverrides[group.rule_number] || group.llm_reasoning;
      const appendText = analysisAppends[group.rule_number];
      const effectiveAnalysis = appendText && baseAnalysis
        ? `${baseAnalysis}\n\n${appendText}`
        : appendText || baseAnalysis;

      const singleAnalysisHtml = effectiveAnalysis && showTopAnalysis ? `
        <div class="reasoning">
          <strong>Analysis:</strong> ${highlightWarning(effectiveAnalysis)}
        </div>
      ` : '';

      // Build Multi-Standard Compliance Index table
      // Helper to extract status from per-standard text (e.g. "measured : 39.62''\n\nwarning")
      const extractStatusFromText = (text: string | null): { label: string; color: string } => {
        if (!text) return { label: '-', color: '#6b7280' };
        const t = text.toLowerCase();
        if (t.includes('fail')) return { label: 'FAIL', color: '#dc2626' };
        if (t.includes('pass')) return { label: 'PASS', color: '#059669' };
        if (t.includes('warning')) return { label: 'WARNING', color: '#d97706' };
        if (t.includes('not test') || t.includes('not appli')) return { label: 'N/A', color: '#6b7280' };
        if (t.includes('insuffi')) return { label: 'INSUFFICIENT DATA', color: '#d97706' };
        return { label: '-', color: '#6b7280' };
      };

      // Detect standard system: FBC (Florida) vs ICC (Texas)
      const isFBC = !!(group.fbc_status_text || group.fbc_value || (jurisdiction && jurisdiction.toLowerCase().includes('fbc')));
      const primaryStandardShort = isFBC ? 'FBC 2023' : 'ICC A117.1';

      // Primary status from the main status field
      const statusUpper2 = group.status.toUpperCase();
      const primaryStatus = (statusUpper2 === 'FAIL' || statusUpper2 === 'FAILED') ? { label: 'FAIL', color: '#dc2626' } :
                         statusUpper2 === 'PASS' ? { label: 'PASS', color: '#059669' } :
                         statusUpper2 === 'WARNING' ? { label: 'WARNING', color: '#d97706' } :
                         { label: statusUpper2, color: '#6b7280' };

      // Per-standard statuses and display values
      const adaStatusRaw = group.ada_status_text ? extractStatusFromText(group.ada_status_text) : primaryStatus;
      // When ADA text says "Same as ..." (primary standard) or has no explicit status, use the rule's primary status
      const adaStatus = (adaStatusRaw.label === '-' && group.ada_status_text?.toLowerCase().includes('same as'))
        ? primaryStatus
        : adaStatusRaw;

      let primaryDisplay: string;
      let secondaryStatus: { label: string; color: string };
      let secondaryDetailText: string;
      let adaDisplay: string;
      let adaDetailText: string;

      if (isFBC) {
        // Florida: FBC 2023 Rule | FBC 2023 Requirement | ADA 2010 | (empty)
        primaryDisplay = group.fbc_value || group.required_value || '-';
        adaDisplay = group.ada_value || '-';
        adaDetailText = group.ada_status_text ? group.ada_status_text.replace(/\n/g, '<br/>') : adaDisplay;
        secondaryStatus = { label: '-', color: '#6b7280' };
        secondaryDetailText = '';
      } else {
        // Texas: ICC A117.1 Rule | ICC Requirement | TAS 2012 | ADA 2010
        primaryDisplay = group.icc_value || group.required_value || '-';
        adaDisplay = group.ada_value || '-';
        adaDetailText = adaDisplay.replace(/\n/g, '<br/>');
        secondaryStatus = group.tas_status_text ? extractStatusFromText(group.tas_status_text) : primaryStatus;
        const tasDisplay = group.tas_value || '-';
        secondaryDetailText = tasDisplay.replace(/\n/g, '<br/>');
      }

      // Determine comparison note for TAS/ADA columns (Texas only)
      const isDifferentFromICC = group.rule_number.startsWith('604.5.1') || group.rule_number.startsWith('604.5.2') || group.rule_number === '609.4.1';
      const comparisonNote = isDifferentFromICC
        ? '<div style="margin-top: 6px; font-weight: 700; color: #dc2626; font-size: 8pt;">Different from ICC A117.1</div>'
        : '<div style="margin-top: 6px; font-weight: 700; color: #374151; font-size: 8pt;">Same as ICC A 117.1</div>';

      // Only show regulation comparison columns when multi-standard data exists
      const hasMultiStandardData = !!(group.fbc_status_text || group.fbc_value || group.tas_status_text || group.tas_value || group.icc_value || group.ada_status_text || group.ada_value);

      const complianceIndexHtml = isFBC ? `
        <div style="margin: 14px 0;">
          <table style="width: 100%; border-collapse: collapse; font-size: 9pt; border: 1px solid #d1d5db;">
            <thead>
              <tr>
                <th colspan="2" style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #1e3a5f; border: 1px solid #d1d5db;">FBC 2023</th>
                ${hasMultiStandardData ? `<th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #1e3a5f; border: 1px solid #d1d5db;">Regulation Comparison</th>` : ''}
              </tr>
              <tr>
                <th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">Rule</th>
                <th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">Requirement</th>
                ${hasMultiStandardData ? `<th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">ADA 2010</th>` : ''}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding: 10px; text-align: center; font-weight: 700; border: 1px solid #d1d5db;">${group.rule_number}</td>
                <td style="padding: 10px; text-align: center; border: 1px solid #d1d5db; white-space: pre-line;">${primaryDisplay}</td>
                ${hasMultiStandardData ? `<td style="padding: 10px; text-align: center; border: 1px solid #d1d5db; white-space: pre-line;">
                  <div style="color: ${adaStatus.color}; font-weight: 700;">${adaStatus.label}</div>
                  <div style="font-size: 8pt; color: #6b7280; margin-top: 2px;">${adaDisplay}</div>
                  <div style="margin-top: 6px; font-weight: 700; color: #374151; font-size: 8pt;">Same as FBC 2023</div>
                </td>` : ''}
              </tr>
            </tbody>
          </table>
        </div>
      ` : `
        <div style="margin: 14px 0;">
          <table style="width: 100%; border-collapse: collapse; font-size: 9pt; border: 1px solid #d1d5db;">
            <thead>
              <tr>
                <th colspan="2" style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #1e3a5f; border: 1px solid #d1d5db;">${primaryStandardShort}</th>
                ${hasMultiStandardData ? `<th colspan="2" style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #1e3a5f; border: 1px solid #d1d5db;">Regulation Comparison</th>` : ''}
              </tr>
              <tr>
                <th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">Rule</th>
                <th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">Requirement</th>
                ${hasMultiStandardData ? `<th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">TAS 2012</th>
                <th style="padding: 8px 10px; text-align: center; font-weight: 700; color: white; background: #374151; border: 1px solid #d1d5db;">ADA 2010</th>` : ''}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding: 10px; text-align: center; font-weight: 700; border: 1px solid #d1d5db;">${group.rule_number}</td>
                <td style="padding: 10px; text-align: center; border: 1px solid #d1d5db; white-space: pre-line;">${primaryDisplay}</td>
                ${hasMultiStandardData ? `<td style="padding: 10px; text-align: center; border: 1px solid #d1d5db; white-space: pre-line;">
                  <div style="color: ${secondaryStatus.color}; font-weight: 700;">${secondaryStatus.label}</div>
                  <div style="font-size: 8pt; color: #6b7280; margin-top: 2px;">${secondaryDetailText}</div>
                  ${comparisonNote}
                </td>
                <td style="padding: 10px; text-align: center; border: 1px solid #d1d5db; white-space: pre-line;">
                  <div style="color: ${adaStatus.color}; font-weight: 700;">${adaStatus.label}</div>
                  <div style="font-size: 8pt; color: #6b7280; margin-top: 2px;">${adaDetailText}</div>
                  ${comparisonNote}
                </td>` : ''}
              </tr>
            </tbody>
          </table>
        </div>
      `;

      // Build location label from affected elements
      const uniqueLocations = [...new Set(group.affectedElements
        .map(el => {
          if (el.roomName) {
            return el.roomNumber ? `Room ${el.roomNumber} - ${el.roomName}` : el.roomName;
          }
          return null;
        })
        .filter(Boolean)
      )];
      const locationLabel = uniqueLocations.length > 0 ? uniqueLocations.join(' · ') : '';

      const groupedCardHtml = `
        <div class="rule-card ${cardType}">
          <div class="rule-header">
            <div>
              <div class="rule-number">${primaryStandardShort} — ${group.rule_number}</div>
              <div class="rule-name">${group.rule_name}</div>
            </div>
            <span class="status-badge ${cardType}">${group.status}</span>
          </div>

          ${locationLabel ? `
            <div style="margin: 6px 0 4px 0; font-size: 10pt; color: #6b7280;">
              <span style="font-weight: 600; color: #4f46e5;">Location:</span> ${locationLabel}
            </div>
          ` : ''}

          ${singleAnalysisHtml}

          ${group.rule_raw_text ? `
            <div class="rule-text">${group.rule_raw_text}</div>
          ` : ''}

          ${complianceIndexHtml}

          <div style="margin: 10px 0;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #374151; font-size: 11pt;">
              Affected Elements (${
                group.affectedElements.reduce((sum, el) => {
                  if (el.entities_used && el.entities_used.length > 0) return sum + el.entities_used.length;
                  if (el.involved_db_ids && el.involved_db_ids.length > 0) return sum + el.involved_db_ids.length;
                  return sum + 1;
                }, 0)
              }):
            </div>
            ${affectedElementsHtml}
          </div>

          <div class="screenshot-container" style="position: relative;">
            <div class="screenshot-title">3D Viewer Screenshot</div>
            ${screenshotData ? `
              <div style="position: relative; display: inline-block; width: 100%;">
                <img src="${screenshotData}" alt="Screenshot for ${group.rule_name}" class="screenshot-image" />
                ${group.measured_value !== null && group.measured_value !== undefined ? (() => {
                  const statusUp = group.status.toUpperCase();
                  const badgeColor = (statusUp === 'FAIL' || statusUp === 'FAILED') ? '#dc2626' :
                                     statusUp === 'PASS' ? '#059669' : '#d97706';
                  const badgeBg = (statusUp === 'FAIL' || statusUp === 'FAILED') ? 'rgba(220,38,38,0.12)' :
                                  statusUp === 'PASS' ? 'rgba(5,150,105,0.12)' : 'rgba(217,119,6,0.12)';
                  const badgeLabel = (statusUp === 'FAIL' || statusUp === 'FAILED') ? 'FAIL' :
                                     statusUp === 'PASS' ? 'PASS' : 'WARNING';
                  const measuredDisplay = String(group.measured_value);
                  const requiredDisplay = group.required_value || '';
                  return `
                    <div style="position: absolute; bottom: 12px; left: 12px; background: rgba(255,255,255,0.95); border: 2px solid ${badgeColor}; border-radius: 8px; padding: 8px 14px; font-family: 'Segoe UI', system-ui, sans-serif; box-shadow: 0 2px 8px rgba(0,0,0,0.15); max-width: 70%;">
                      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                        <span style="background: ${badgeBg}; color: ${badgeColor}; font-size: 8pt; font-weight: 700; padding: 2px 8px; border-radius: 4px; letter-spacing: 0.5px;">${badgeLabel}</span>
                        <span style="font-size: 9pt; font-weight: 700; color: #1f2937;">${group.rule_number} — ${group.rule_name}</span>
                      </div>
                      <div style="font-size: 9pt; color: #374151; line-height: 1.4;">
                        <span style="font-weight: 600;">Measured:</span> <span style="color: ${badgeColor}; font-weight: 700;">${measuredDisplay}</span>
                        ${requiredDisplay ? `<br/><span style="font-weight: 600;">Required:</span> ${requiredDisplay}` : ''}
                      </div>
                    </div>
                  `;
                })() : ''}
              </div>
            ` : `
              <div class="screenshot-placeholder">Screenshot not available</div>
            `}
          </div>
        </div>
      `;

      // Each rule card starts on a new page to ensure nothing is cut off.
      // Skylight: split the card into content + screenshot so that the tall
      // element table can span multiple pages while the screenshot is never
      // cut in half.  We split at the screenshot-container div boundary.
      const isSkylight2 = displayName?.toLowerCase() === 'skylight';
      if (isSkylight2) {
        const splitMarker = '<div class="screenshot-container"';
        const splitIdx = groupedCardHtml.indexOf(splitMarker);
        if (splitIdx > 0) {
          // Content part: everything before the screenshot (close the wrapper div)
          const contentHtml = groupedCardHtml.substring(0, splitIdx) + '</div>';
          // Screenshot part: the screenshot container (re-open with card wrapper)
          const screenshotHtml = '<div class="rule-card">' + groupedCardHtml.substring(splitIdx);

          await addSectionToPDFSliced(contentHtml, true);
          await addSectionToPDF(screenshotHtml, false);
        } else {
          // No screenshot — just slice the whole card
          await addSectionToPDFSliced(groupedCardHtml, true);
        }
      } else {
        await addSectionToPDF(groupedCardHtml, true);
      }
    }

    onProgress?.(98, 'Finalizing PDF...');

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `ADA-Compliance-Report-Combined-${timestamp}.pdf`;
    pdf.save(filename);

    onProgress?.(100, 'Download ready!');
  }

}

// Dev-only: expose captureViewerScreenshot for quick camera iteration
// from Chrome DevTools / MCP. Usage:
//   await window.__pdfDebugCapture([dbId1, dbId2], 'FAIL', true /* isDoor */)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).__pdfDebugCapture = async (
    dbIds: number[],
    status: string = 'FAIL',
    isDoor: boolean = false,
  ): Promise<string> => {
    const viewer = (window as any).NOP_VIEWER;
    if (!viewer) throw new Error('no viewer loaded');
    return await (ComplianceReportPDF as any).captureViewerScreenshot(
      viewer, dbIds, [], status, isDoor, null, null, false, false,
    );
  };
}
